// Conținutul manualului de utilizare.
// Fiecare secțiune are un id, titlu, categorie și o listă de roluri cărora li se adresează.
// Lăsăm conținutul în format array de blocuri pentru a-l putea extinde ușor (paragraph, list, tip, etc.).
//
// Roluri disponibile:
//   SUPER_ADMIN, SSM_COMPANY_ADMIN, SSM_INSTRUCTOR,
//   COMPANY_ADMIN, DIRECTOR_GENERAL, EMPLOYEE
//
// Notă: textul marcat ca "TODO" este placeholder — se completează incremental.

export const ALL_ROLES = [
  'SUPER_ADMIN',
  'SSM_COMPANY_ADMIN',
  'SSM_INSTRUCTOR',
  'COMPANY_ADMIN',
  'DIRECTOR_GENERAL',
  'EMPLOYEE',
];

// Categorii principale afișate în cuprins — mirror al grupurilor din bara laterală
export const MANUAL_CATEGORIES = [
  { id: 'getting-started', label: { ro: 'Primii pași', en: 'Getting started' } },
  { id: 'account', label: { ro: 'Contul meu', en: 'My account' } },
  { id: 'personal', label: { ro: 'Zona mea personală', en: 'My personal area' } },
  { id: 'companies', label: { ro: 'Firme și organizare', en: 'Companies & organization' } },
  { id: 'employees', label: { ro: 'Angajați', en: 'Employees' } },
  { id: 'courses', label: { ro: 'Cursuri și instruiri', en: 'Courses & trainings' } },
  { id: 'sheets', label: { ro: 'Fișe SSM', en: 'SSM sheets' } },
  { id: 'reports', label: { ro: 'Notificări și rapoarte', en: 'Notifications & reports' } },
  { id: 'admin', label: { ro: 'Administrare platformă', en: 'Platform administration' } },
  { id: 'faq', label: { ro: 'Întrebări frecvente', en: 'FAQ' } },
];

// Helper pentru blocurile de conținut.
// Fiecare helper acceptă opțional { onlyRoles } ca al treilea argument —
// dacă e setat, blocul e afișat DOAR utilizatorilor cu unul dintre rolurile listate.
const p = (ro, en, opts = {}) => ({ type: 'paragraph', ro, en, ...opts });
const h = (ro, en, opts = {}) => ({ type: 'heading', ro, en, ...opts });
const list = (roItems, enItems, opts = {}) => ({ type: 'list', ro: roItems, en: enItems, ...opts });
const tip = (ro, en, opts = {}) => ({ type: 'tip', ro, en, ...opts });
const steps = (roItems, enItems, opts = {}) => ({
  type: 'steps',
  ro: roItems,
  en: enItems,
  ...opts,
});

// SECȚIUNI — fiecare are id unic, categorie, roluri și conținut
export const MANUAL_SECTIONS = [
  // ── PRIMII PAȘI ────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    category: 'getting-started',
    roles: ALL_ROLES,
    title: { ro: 'Dashboard-ul principal', en: 'Main dashboard' },
    keywords: {
      ro: ['dashboard', 'prima pagina', 'pagina principala', 'panoul de control', 'sumar'],
      en: ['dashboard', 'home page', 'main page', 'control panel', 'summary'],
    },
    content: [
      p(
        'Dashboard-ul este prima pagină pe care o vezi la autentificare. Conținutul lui este adaptat rolului tău și îți oferă o privire rapidă asupra lucrurilor importante din ziua ta.',
        'The dashboard is the first page you see after logging in. Its content is tailored to your role and gives you a quick overview of the important items of the day.'
      ),
      p(
        'Ca angajat vezi rapid câte cursuri ai de parcurs și câte certificări ai deja obținute, plus un link direct către „Cursurile mele".',
        'As an employee you quickly see how many courses you have to take and how many certificates you already hold, plus a direct link to "My trainings".',
        { onlyRoles: ['EMPLOYEE', 'COMPANY_ADMIN', 'DIRECTOR_GENERAL'] }
      ),
      p(
        'Ca administrator vezi starea de ansamblu a firmelor sau angajaților tăi — câți au instruirea la zi, câți au IP-ul expirat, ultimele activități importante.',
        'As an administrator you see the overall status of your companies or employees — how many are up to date, how many have expired IP, and the latest important activities.',
        { onlyRoles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN'] }
      ),
      tip(
        'Dacă vrei o imagine detaliată despre cine are IP expirat, intră în „Semaforizare IP" din meniu — acolo ai filtrele complete și poți trimite remindere rapid.',
        'If you want a detailed view of who has expired IP, open "IP traffic-light" from the menu — there you have full filters and can send reminders quickly.',
        { onlyRoles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN'] }
      ),
      tip(
        'Poți reveni oricând la dashboard apăsând numele firmei sau logo-ul din bara de sus.',
        'You can return to the dashboard anytime by pressing the company name or the logo in the top bar.'
      ),
    ],
  },
  {
    id: 'welcome',
    category: 'getting-started',
    roles: ALL_ROLES,
    title: { ro: 'Bun venit pe SSMPlatform', en: 'Welcome to SSMPlatform' },
    content: [
      p(
        'SSMPlatform este platforma prin care compania ta gestionează instruirea în domeniul Securității și Sănătății în Muncă (SSM). Prin platformă se atribuie cursurile, se urmăresc progresele, se semnează fișele de instruire și se emit certificatele — totul digital, fără hârtii.',
        'SSMPlatform is the platform your company uses to manage Occupational Health & Safety (OHS) training. You assign courses, track progress, sign training sheets and issue certificates — all digitally, paperless.'
      ),
      p(
        'Acest manual te ghidează prin funcționalitățile disponibile în contul tău. Secțiunile afișate mai jos sunt deja filtrate în funcție de rolul cu care ești autentificat — vezi doar ce te privește.',
        'This manual walks you through the features available in your account. The sections below are already filtered by your login role — you only see what is relevant to you.'
      ),
      h('Cum folosești manualul', 'How to use the manual'),
      list(
        [
          'Folosește bara de căutare din partea de sus pentru a găsi rapid un subiect (ex: „semnătură", „test", „certificat").',
          'Cuprinsul din stânga te duce direct la secțiunea dorită.',
          'Dacă nu găsești răspunsul aici, contactează administratorul firmei tale sau responsabilul SSM.',
        ],
        [
          'Use the search bar at the top to quickly find a topic (e.g. "signature", "test", "certificate").',
          'The table of contents on the left jumps straight to the section you need.',
          "If you can't find the answer here, contact your company administrator or SSM officer.",
        ]
      ),
      tip(
        'Contul tău de SUPER_ADMIN are acces la toate firmele din platformă. Folosește „Căutare" din meniu pentru a naviga rapid între ele.',
        'Your SUPER_ADMIN account has access to all companies on the platform. Use "Search" in the menu to navigate between them quickly.',
        { onlyRoles: ['SUPER_ADMIN'] }
      ),
    ],
  },
  {
    id: 'first-login',
    category: 'getting-started',
    roles: ALL_ROLES,
    title: { ro: 'Prima autentificare', en: 'First login' },
    content: [
      p(
        'Primul tău contact cu platforma este pagina de login, unde introduci adresa de email și parola primite de la administratorul firmei. După autentificare, platforma te ghidează printr-un mic proces de configurare inițială, pentru ca profilul tău să fie funcțional.',
        'Your first touch with the platform is the login page, where you enter the email and password received from your company administrator. After signing in, the platform walks you through a short setup process so your profile is ready to use.'
      ),
      h('Pașii de configurare inițială', 'Initial setup steps'),
      steps(
        [
          'Schimbarea parolei — la prima autentificare ți se cere obligatoriu să setezi o parolă nouă, cunoscută doar de tine. Parola inițială primită pe email nu mai este valabilă după acest pas.',
          'Configurarea semnăturii digitale — semnezi o singură dată într-o casetă dedicată. Această semnătură va fi aplicată automat pe fișele și documentele pe care le semnezi ulterior.',
          'Completarea traseului casă–muncă — introduci adresa de domiciliu, adresa locului de muncă și mijlocul de transport folosit. Aceste date sunt necesare pentru fișa de instruire a traseului.',
        ],
        [
          'Change password — on first login you are required to set a new password, known only to you. The initial password received by email is no longer valid after this step.',
          'Configure digital signature — you sign once in a dedicated box. This signature is then automatically applied to the sheets and documents you sign afterwards.',
          'Fill in the home–work route — enter your home address, workplace address and the means of transport you use. This data is required for the route training sheet.',
        ]
      ),
      p(
        'Dacă amâni un pas (de exemplu traseul), platforma îți va aminti la următoarea autentificare. Poți finaliza oricând configurarea din meniul „Profilul meu" și „Traseul meu".',
        'If you skip a step (for example the route), the platform will remind you on your next login. You can always complete the setup from the "My profile" and "My route" menu entries.'
      ),
      tip(
        'Ca SUPER_ADMIN nu ți se cere traseul casă–muncă — acest pas apare doar pentru rolurile care au nevoie de fișa de traseu.',
        'As SUPER_ADMIN you are not asked for a home–work route — this step only appears for roles that need the route sheet.',
        { onlyRoles: ['SUPER_ADMIN'] }
      ),
      tip(
        'Dacă ai uitat parola înainte să ajungi la prima autentificare, folosește „Am uitat parola" din pagina de login pentru a primi un link de resetare pe email.',
        'If you forgot your password before reaching first login, use "Forgot password" on the login page to receive a reset link by email.'
      ),
    ],
  },

  // ── CONTUL MEU ─────────────────────────────────────────────────────────
  {
    id: 'my-profile',
    category: 'account',
    roles: ALL_ROLES,
    title: { ro: 'Profilul meu', en: 'My profile' },
    content: [
      p(
        'Pagina „Profilul meu" este centrul contului tău — aici vezi toate datele care te descriu în platformă și de aici ajungi la setările legate de persoana ta. Accesezi pagina din meniul fix de jos, de sub listele de navigație.',
        'The "My profile" page is your account hub — here you see all the data that describes you on the platform, and from here you reach your personal settings. You open it from the pinned menu at the bottom of the sidebar.'
      ),
      h('Ce vezi în pagină', 'What you see on the page'),
      list(
        [
          'Informații personale: nume, prenume, email, telefon.',
          'Informații de angajare: funcție, departament, data angajării, firmă.',
          'Traseul casă–muncă: adresele și mijlocul de transport folosit, cu buton de editare rapidă.',
          'Statistici instruiri: câte cursuri ai atribuite, câte sunt finalizate și procentul de finalizare.',
        ],
        [
          'Personal info: first name, last name, email, phone.',
          'Employment info: job title, department, hire date, company.',
          'Home–work route: addresses and the means of transport used, with a quick edit button.',
          'Training stats: number of courses assigned, number completed, and completion rate.',
        ]
      ),
      tip(
        'Ca SSM_COMPANY_ADMIN, SSM_INSTRUCTOR sau SUPER_ADMIN, tot din această pagină activezi sau dezactivezi reminderele pentru instruirile periodice ale angajaților pe care îi urmărești.',
        'As SSM_COMPANY_ADMIN, SSM_INSTRUCTOR or SUPER_ADMIN, the same page is where you enable or disable reminders for the periodic trainings of the employees you monitor.',
        { onlyRoles: ['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'SUPER_ADMIN'] }
      ),
      p(
        'Datele personale și de angajare sunt introduse de administratorul firmei și nu pot fi modificate direct de tine — dacă observi o eroare (ex: funcție greșită), semnalează-i administratorului ca să o corecteze.',
        'Personal and employment data are entered by your company administrator and cannot be edited directly by you — if you notice a mistake (e.g. wrong job title), ask the administrator to correct it.'
      ),
    ],
  },
  {
    id: 'change-password',
    category: 'account',
    roles: ALL_ROLES,
    title: { ro: 'Schimbarea parolei', en: 'Change password' },
    content: [
      p(
        'Parola ta este singurul mod prin care platforma verifică cine ești — păstreaz-o confidențială și schimbă-o imediat ce bănuiești că altcineva o cunoaște. Poți schimba parola oricând, nu doar la prima autentificare.',
        'Your password is the only way the platform verifies who you are — keep it confidential and change it immediately if you suspect someone else knows it. You can change your password any time, not only at first login.'
      ),
      h('Cum schimbi parola', 'How to change your password'),
      steps(
        [
          'Deschide modalul de schimbare a parolei (la prima autentificare apare automat; ulterior îl poți deschide din setările de cont).',
          'Introdu parola actuală pentru confirmare.',
          'Introdu parola nouă — minim 8 caractere, diferită de cea actuală.',
          'Reintrodu parola nouă pentru confirmare.',
          'Apasă „Salvează" — vei fi delogat și vei intra din nou cu parola nouă.',
        ],
        [
          'Open the change-password dialog (it appears automatically on first login; afterwards you open it from account settings).',
          'Enter your current password for confirmation.',
          'Enter the new password — at least 8 characters, different from the current one.',
          'Re-enter the new password to confirm.',
          'Press "Save" — you will be signed out and log back in with the new password.',
        ]
      ),
      tip(
        'Dacă ți-ai uitat parola înainte să te poți autentifica, folosește „Am uitat parola" din pagina de login — vei primi pe email un link prin care îți setezi una nouă.',
        'If you forgot your password before you can sign in, use "Forgot password" on the login page — you will receive an email link to set a new one.'
      ),
    ],
  },
  {
    id: 'my-signature',
    category: 'account',
    roles: ALL_ROLES,
    title: { ro: 'Semnătura digitală', en: 'Digital signature' },
    keywords: {
      ro: [
        'semnare',
        'semnez',
        'semnat',
        'semnătură',
        'semn',
        'signature',
        'desenez',
        'scriu semnătura',
        'configurare semnătură',
      ],
      en: ['sign', 'signing', 'signed', 'signature', 'draw signature', 'type signature'],
    },
    content: [
      p(
        'Semnătura digitală înlocuiește semnătura olografă pe documentele din platformă: fișe de instruire, fișe de traseu, fișe colective. O configurezi o singură dată, iar platforma o aplică automat oriunde este nevoie de semnătura ta.',
        'The digital signature replaces your handwritten signature on platform documents: training sheets, route sheets, collective sheets. You configure it once, and the platform applies it automatically wherever your signature is required.'
      ),
      h('Două moduri de a-ți crea semnătura', 'Two ways to create your signature'),
      list(
        [
          '„Desenez" — folosești mouse-ul sau degetul (pe dispozitive touch) pentru a semna într-o casetă. E cel mai apropiat de semnătura ta reală.',
          '„Scriu" — platforma îți afișează numele complet în trei stiluri caligrafice la alegere (Dancing Script, Great Vibes, Pacifico). Alegi cel care îți place și salvezi.',
        ],
        [
          '"Draw" — you use the mouse or your finger (on touch devices) to sign inside a box. This is closest to your real handwritten signature.',
          '"Type" — the platform renders your full name in three calligraphic styles (Dancing Script, Great Vibes, Pacifico). Pick the one you like and save.',
        ]
      ),
      h('Cum configurezi semnătura', 'How to set up the signature'),
      steps(
        [
          'La prima autentificare modalul de semnătură apare automat după schimbarea parolei.',
          'Alege modul „Desenez" sau „Scriu".',
          'Creează semnătura — dacă desenezi și nu îți place, folosește „Șterge" pentru a reîncepe.',
          'Salvează. Semnătura este păstrată sub contul tău și folosită pentru toate documentele viitoare.',
        ],
        [
          'On first login the signature dialog appears automatically after the password change.',
          'Choose "Draw" or "Type".',
          'Create the signature — if you draw and don\'t like the result, press "Clear" to start over.',
          'Save. The signature is stored on your account and used for all future documents.',
        ]
      ),
      tip(
        'Poți modifica oricând semnătura din „Profilul meu". Documentele deja semnate rămân cu semnătura de la momentul semnării — nu se modifică retroactiv.',
        'You can change the signature any time from "My profile". Documents already signed keep the signature used at the time of signing — they are not updated retroactively.'
      ),
    ],
  },
  {
    id: 'my-route',
    category: 'account',
    roles: ['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'DIRECTOR_GENERAL', 'EMPLOYEE'],
    title: { ro: 'Traseul casă–muncă', en: 'Home–work route' },
    content: [
      p(
        'Fișa de traseu este un document obligatoriu conform legislației SSM din România — ea descrie drumul tău zilnic între domiciliu și locul de muncă și este necesară pentru evaluarea riscurilor de accidente de traseu. Platforma generează această fișă automat pe baza datelor pe care le completezi aici.',
        'The route sheet is a document required under Romanian OHS law — it describes your daily commute between home and workplace and is used for assessing commute accident risks. The platform generates this sheet automatically from the data you fill in here.'
      ),
      h('Ce trebuie să completezi', 'What you need to fill in'),
      list(
        [
          'Adresa de domiciliu — obligatoriu.',
          'Adresa locului de muncă — obligatoriu.',
          'Mijlocul de transport: autoturism personal, transport în comun, mixt, pe jos, bicicletă sau motocicletă.',
          'Durata traseului (minute) — opțional, dar util pentru o evaluare mai exactă.',
          'Detalii suplimentare — opțional (ex: traversează o intersecție periculoasă, folosește două mijloace de transport).',
        ],
        [
          'Home address — required.',
          'Workplace address — required.',
          'Means of transport: personal car, public transport, mixed, walking, bicycle or motorcycle.',
          'Commute duration (minutes) — optional, but helpful for a more accurate assessment.',
          'Additional details — optional (e.g. crosses a dangerous intersection, uses two means of transport).',
        ]
      ),
      h('Când apare fluxul', 'When the flow appears'),
      p(
        'La prima autentificare platforma îți cere să completezi traseul imediat după semnătură. Dacă amâni („Mai târziu"), reminderul va reapărea la următoarele autentificări până la completare. Poți edita oricând datele din „Profilul meu" → „Editează traseul" sau din pagina „Traseul meu".',
        'On first login the platform asks you to fill in the route right after the signature step. If you postpone ("Later"), the reminder will reappear on subsequent logins until completed. You can edit the data any time from "My profile" → "Edit route" or from the "My route" page.'
      ),
      tip(
        'Dacă te muți sau îți schimbi locul de muncă, actualizează imediat datele — fișa veche rămâne arhivată, dar cea nouă se generează cu datele curente.',
        'If you move or change workplace, update the data immediately — the old sheet remains archived, but the new one is generated with the current data.'
      ),
    ],
  },

  // ── CURSURI ────────────────────────────────────────────────────────────
  {
    id: 'my-courses',
    category: 'personal',
    roles: ['EMPLOYEE', 'COMPANY_ADMIN', 'DIRECTOR_GENERAL'],
    title: { ro: 'Cursurile mele', en: 'My trainings' },
    content: [
      p(
        'Pagina „Cursurile mele" este locul central de unde îți gestionezi instruirea. Aici apar toate cursurile care ți-au fost atribuite de responsabilul SSM sau de administratorul firmei, grupate logic pentru a-ți arăta ce trebuie să faci în continuare.',
        'The "My trainings" page is the central place where you manage your training. All courses assigned to you by the SSM officer or company administrator appear here, grouped logically to show you what to do next.'
      ),
      h('Tipurile de cursuri pe care le vei vedea', 'The course types you will see'),
      list(
        [
          'Instruire Introductiv Generală (IIG) — cursul inițial, obligatoriu la angajare.',
          'Instruire la Locul de Muncă (ILM) — despre riscurile specifice postului tău.',
          'Instruire Periodică (IP) — se reia la intervale stabilite (ex: anual).',
          'Instruire Suplimentară — atribuită la schimbări de post, accidente sau echipament nou.',
        ],
        [
          'General Introductory Training (IIG) — the initial course, mandatory on hire.',
          'Workplace Training (ILM) — about the specific risks of your job.',
          'Periodic Training (IP) — repeated at set intervals (e.g. yearly).',
          'Supplementary Training — assigned on job changes, accidents or new equipment.',
        ]
      ),
      p(
        'Fiecare tip apare cu două variante: SSM (Securitate și Sănătate în Muncă) și SU (Situații de Urgență). Cele două se parcurg separat, dar fluxul este același.',
        'Each type comes in two variants: SSM (Occupational Health & Safety) and SU (Emergency Situations). You take them separately, but the flow is identical.'
      ),
      h('Ordinea obligatorie', 'The mandatory order'),
      p(
        'Cursurile inițiale se parcurg într-o ordine legală fixă: IIG SSM → ILM SSM → IIG SU → ILM SU. Platforma blochează automat cursul următor până când îl promovezi pe cel anterior — vei vedea un lacăt pe cardul cursurilor blocate. Cursurile periodice și suplimentare nu depind de această ordine.',
        'Initial courses follow a fixed legal order: IIG SSM → ILM SSM → IIG SU → ILM SU. The platform automatically locks the next course until you pass the previous one — you will see a lock icon on locked course cards. Periodic and supplementary courses do not depend on this order.'
      ),
      h('Ce vezi pe fiecare card de curs', 'What you see on each course card'),
      list(
        [
          'Titlul și tipul cursului (IIG / ILM / IP / Suplimentară).',
          'Statusul: Neînceput, În desfășurare, Finalizat sau Picat.',
          'Data-limită, dacă există.',
          'Butonul de acțiune: „Începe", „Continuă", „Vezi certificatul" sau „Reia testul".',
        ],
        [
          'Title and course type (IIG / ILM / IP / Supplementary).',
          'Status: Not started, In progress, Completed or Failed.',
          'Deadline, if set.',
          'Action button: "Start", "Continue", "View certificate" or "Retry test".',
        ]
      ),
      tip(
        'Ca COMPANY_ADMIN sau DIRECTOR_GENERAL, chiar dacă administrezi firma, treci prin același flux ca angajații pentru cursurile atribuite ție — legea cere ca fiecare persoană să semneze propria fișă de instruire.',
        'As COMPANY_ADMIN or DIRECTOR_GENERAL, even though you administer the company, you go through the same flow as employees for courses assigned to you — the law requires each person to sign their own training sheet.',
        { onlyRoles: ['COMPANY_ADMIN', 'DIRECTOR_GENERAL'] }
      ),
    ],
  },
  {
    id: 'taking-a-course',
    category: 'personal',
    roles: ['EMPLOYEE', 'COMPANY_ADMIN', 'DIRECTOR_GENERAL'],
    title: { ro: 'Cum parcurgi un curs', en: 'Taking a course' },
    keywords: {
      ro: [
        'incepe curs',
        'porneste curs',
        'module video',
        'intrebari interactive',
        'drag and drop',
        'potrivire',
        'ordonare',
        'progres curs',
      ],
      en: [
        'start course',
        'begin course',
        'video modules',
        'interactive questions',
        'course progress',
      ],
    },
    content: [
      p(
        'Parcurgerea unui curs este un proces liniar: urmărești modulele video, răspunzi la întrebările interactive, susții testul final și semnezi fișa de instruire. Dacă închizi fereastra la jumătate, platforma îți reține poziția și reiei exact de unde ai rămas.',
        'Taking a course is a linear process: you watch the video modules, answer the interactive questions, take the final test and sign the training sheet. If you close the window halfway, the platform remembers your position and you resume exactly where you left off.'
      ),
      h('Pașii pe care îi parcurgi', 'The steps you go through'),
      steps(
        [
          'Pornești cursul apăsând „Începe" pe cardul cursului din „Cursurile mele".',
          'Urmărești fiecare modul video până la capăt — platforma blochează sărirea înainte, trebuie să vezi tot conținutul.',
          'Răspunzi la întrebările interactive care apar în timpul sau la finalul unor module (drag & drop, potrivire, ordonare, alegere multiplă).',
          'După ce toate modulele sunt finalizate se deblochează testul final.',
          'Susții testul final și, dacă îl promovezi, treci la semnarea fișei de instruire.',
          'Fișa semnată este arhivată automat, iar certificatul este generat și disponibil în „Certificările mele".',
        ],
        [
          'You start the course by pressing "Start" on the course card in "My trainings".',
          'You watch each video module to the end — the platform prevents skipping ahead, you must see all the content.',
          'You answer the interactive questions that appear during or after modules (drag & drop, matching, ordering, multiple choice).',
          'Once all modules are complete, the final test unlocks.',
          'You take the final test and, if you pass, move on to signing the training sheet.',
          'The signed sheet is archived automatically, and the certificate is generated and available in "My certificates".',
        ]
      ),
      tip(
        'Poți întrerupe oricând și reveni mai târziu — progresul tău este salvat automat după fiecare modul și după fiecare răspuns la o întrebare interactivă.',
        'You can pause at any time and come back later — your progress is saved automatically after each module and each interactive answer.'
      ),
    ],
  },
  {
    id: 'final-test',
    category: 'personal',
    roles: ['EMPLOYEE', 'COMPANY_ADMIN', 'DIRECTOR_GENERAL'],
    title: { ro: 'Testul final', en: 'Final test' },
    content: [
      p(
        'Testul final este pasul de evaluare care certifică faptul că ai înțeles conținutul cursului. Se deblochează automat după ce ai terminat toate modulele și este obligatoriu pentru emiterea certificatului.',
        'The final test is the assessment step that certifies you have understood the course content. It unlocks automatically once you finish all modules and is mandatory for issuing the certificate.'
      ),
      h('Ce să știi înainte să începi', 'What to know before you start'),
      list(
        [
          'Scorul minim de promovare este de obicei 80% (poate varia în funcție de setările cursului).',
          'Întrebările pot fi de tip alegere multiplă, bifare, drag & drop, potrivire sau ordonare.',
          'Vezi în timp real câte întrebări ai de rezolvat și câte ai răspuns deja.',
          'La final primești un scor procentual și rezultatul: Promovat sau Picat.',
        ],
        [
          'The minimum passing score is usually 80% (may vary based on course settings).',
          'Questions can be multiple choice, checkboxes, drag & drop, matching or ordering.',
          'You see in real time how many questions remain and how many you have answered.',
          'At the end you receive a percentage score and the result: Passed or Failed.',
        ]
      ),
      h('Dacă nu promovezi', "If you don't pass"),
      p(
        'Dacă scorul este sub minimul necesar, cursul primește statusul „Picat" și poți relua testul. Numărul de reîncercări depinde de setările cursului. Dacă nu reușești nici după ultima încercare, responsabilul SSM trebuie să-ți reatribuie cursul.',
        'If your score is below the required minimum, the course gets the "Failed" status and you can retry the test. The number of retries depends on the course settings. If you still don\'t pass after the last attempt, the SSM officer must reassign the course to you.'
      ),
      tip(
        'Citește întrebările cu atenție — unele par similare cu altele dar au o diferență subtilă. Dacă nu ești sigur pe un răspuns, ia-ți timp să revezi modulul corespunzător înainte să dai testul.',
        'Read the questions carefully — some look similar but have a subtle difference. If you are unsure about an answer, take the time to review the matching module before taking the test.'
      ),
    ],
  },
  {
    id: 'my-certificates',
    category: 'personal',
    roles: ['EMPLOYEE', 'COMPANY_ADMIN', 'DIRECTOR_GENERAL'],
    title: { ro: 'Certificările mele', en: 'My certificates' },
    content: [
      p(
        'După ce promovezi testul și semnezi fișa de instruire, platforma generează automat un certificat digital pentru cursul respectiv. Toate certificatele tale sunt colectate în pagina „Certificările mele", accesibilă din meniu.',
        'After you pass the test and sign the training sheet, the platform automatically generates a digital certificate for that course. All your certificates are collected on the "My certificates" page, accessible from the menu.'
      ),
      h('Ce găsești în fiecare certificat', 'What each certificate contains'),
      list(
        [
          'Numele tău și funcția.',
          'Denumirea și tipul cursului.',
          'Data emiterii și data de expirare (pentru instruirile cu valabilitate — ex: IP anuale).',
          'Scorul obținut la testul final.',
          'Numele firmei și al responsabilului SSM.',
        ],
        [
          'Your name and job title.',
          'Course name and type.',
          'Issue date and expiry date (for courses with validity — e.g. yearly IP).',
          'Score obtained on the final test.',
          'Company name and SSM officer name.',
        ]
      ),
      p(
        'Poți descărca fiecare certificat ca PDF și îl poți prezenta oriunde este necesar (control, audit, angajator nou). Certificatul este dovada oficială că ai parcurs instruirea.',
        'You can download each certificate as a PDF and present it wherever required (inspection, audit, new employer). The certificate is the official proof that you completed the training.'
      ),
      tip(
        'Certificatele pentru instruirile periodice expiră — verifică data de expirare și așteaptă atribuirea noii instruiri din partea responsabilului SSM. Platforma trimite reminder automat cu 30, 14 și 7 zile înainte de expirare.',
        'Certificates for periodic trainings expire — check the expiry date and wait for the SSM officer to assign the new training. The platform sends an automatic reminder 30, 14 and 7 days before expiry.'
      ),
    ],
  },
  {
    id: 'courses-library',
    category: 'courses',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: { EMPLOYEE: ['managesDepartment'] },
    title: {
      ro: 'Meniul „Cursuri" (biblioteca de instruire)',
      en: 'The "Courses" menu (training library)',
    },
    keywords: {
      ro: [
        'biblioteca cursuri',
        'lista cursuri',
        'catalog cursuri',
        'cursuri disponibile',
        'cursuri SSM',
        'cursuri SU',
      ],
      en: ['course library', 'courses list', 'course catalog', 'available courses'],
    },
    content: [
      p(
        'Meniul „Cursuri" este biblioteca de instruiri a firmei tale — toate materialele de curs care pot fi atribuite angajaților. Aici vezi ce e disponibil, cine a creat fiecare curs, pentru ce tip de instruire este (SSM sau SU) și poți accesa statisticile fiecăruia.',
        "The \"Courses\" menu is your company's training library — all course materials that can be assigned to employees. Here you see what's available, who created each course, what training type it covers (SSM or SU), and you can access each course's statistics."
      ),
      h('Ce vezi în listă', 'What you see in the list'),
      list(
        [
          'Titlul fiecărui curs și tipul lui (IIG, ILM, IP, Suplimentară — SSM sau SU).',
          'Durata estimată și numărul de module video.',
          'Firma care deține cursul (cursurile „de sistem" sunt disponibile tuturor).',
          'Butoane rapide: Vezi conținutul, Editează, Statistici, Șterge.',
        ],
        [
          'Each course title and its type (IIG, ILM, IP, Supplementary — SSM or SU).',
          'Estimated duration and number of video modules.',
          'The company that owns the course (system courses are available to everyone).',
          'Quick buttons: View content, Edit, Stats, Delete.',
        ]
      ),
      p(
        'Folosește filtrele din partea de sus ca să rafinezi lista după tip, firmă, serviciu (SSM/SU) sau autor. La firmele mari poți ajunge la sute de cursuri — filtrele sunt prietene.',
        'Use the filters at the top to narrow down by type, company, service (SSM/SU) or author. Large companies can easily reach hundreds of courses — the filters are your friend.'
      ),
      h('Crearea unui curs nou', 'Creating a new course', {
        onlyRoles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'],
      }),
      p(
        'Dacă ai dreptul să creezi cursuri, apasă „Curs nou" din dreapta sus. Fluxul e ghidat pas cu pas: completezi metadatele, configurezi testul final, adaugi module video cu întrebări interactive, previzualizezi și salvezi. La final, cursul apare în bibliotecă și poate fi atribuit angajaților.',
        'If you have rights to create courses, press "New course" at the top right. The flow is guided step by step: fill in metadata, configure the final test, add video modules with interactive questions, preview and save. At the end, the course appears in the library and can be assigned to employees.',
        { onlyRoles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'] }
      ),
      steps(
        [
          'Completează titlul, tipul (IIG/ILM/IP/Suplimentară — SSM sau SU), descrierea și durata estimată.',
          'Configurează testul final: scorul minim de promovare (implicit 80%), numărul de reîncercări permise.',
          'Adaugă modulele — fiecare modul are titlu, text descriptiv și opțional un video (până la 500 MB per fișier).',
          'Adaugă întrebări interactive (alegere multiplă, bifare, drag & drop, potrivire, ordonare) în module sau în testul final.',
          'Previzualizează cursul ca să verifici totul.',
          'Salvează. Cursul apare imediat în bibliotecă.',
        ],
        [
          'Fill in the title, type (IIG/ILM/IP/Supplementary — SSM or SU), description and estimated duration.',
          'Configure the final test: minimum passing score (default 80%), number of retries allowed.',
          'Add modules — each has a title, a descriptive text and optionally a video (up to 500 MB per file).',
          'Add interactive questions (multiple choice, checkboxes, drag & drop, matching, ordering) in modules or in the final test.',
          'Preview the course to verify everything.',
          'Save. The course appears immediately in the library.',
        ],
        { onlyRoles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'] }
      ),
      tip(
        'Pentru videoclipuri mai lungi de câteva minute, împarte-le pe mai multe module. Angajatul parcurge mai ușor și tu poți introduce întrebări interactive între module pentru a verifica înțelegerea.',
        'For videos longer than a few minutes, split them across multiple modules. The employee follows more easily and you can add interactive questions between modules to check comprehension.',
        { onlyRoles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'] }
      ),
      tip(
        'Nu poți crea cursuri — poți doar vedea și atribui cursurile din bibliotecă. Cursurile sunt create de firma SSM care îți deservește firma. Dacă ai nevoie de un curs care nu există, cere-i firmei SSM să-l adauge.',
        "You cannot create courses — you can only view and assign courses from the library. Courses are created by the SSM company that serves yours. If you need a course that doesn't exist, ask the SSM company to add it.",
        { onlyRoles: ['COMPANY_ADMIN'] }
      ),
      tip(
        'Ca CLM, vezi biblioteca completă a firmei, dar nu poți crea cursuri — doar atribui existentele la echipa ta. Creatorii sunt firma SSM sau adminii firmei.',
        "As a CLM, you see the company's full library but cannot create courses — only assign existing ones to your team. Creators are the SSM company or the company admins.",
        { onlyLabels: ['managesDepartment'] }
      ),
    ],
  },
  {
    id: 'templates-library',
    category: 'courses',
    // Doar responsabili SSM/PSI — SSM admin/instructor automat; restul doar cu flag-uri
    roles: [
      'SUPER_ADMIN',
      'SSM_COMPANY_ADMIN',
      'SSM_INSTRUCTOR',
      'COMPANY_ADMIN',
      'DIRECTOR_GENERAL',
      'EMPLOYEE',
    ],
    requireLabelsForRoles: {
      COMPANY_ADMIN: ['isSSMInstructor', 'isCadruTehnicPSI'],
      DIRECTOR_GENERAL: ['isSSMInstructor', 'isCadruTehnicPSI'],
      EMPLOYEE: ['isSSMInstructor', 'isCadruTehnicPSI'],
    },
    title: {
      ro: 'Biblioteca de template-uri (cursuri gata făcute)',
      en: 'Template library (ready-made courses)',
    },
    keywords: {
      ro: [
        'template-uri',
        'cursuri gata',
        'șabloane',
        'biblioteca template',
        'IIG gata',
        'curs gata făcut',
        'clonează curs',
      ],
      en: ['templates', 'ready-made courses', 'course templates', 'clone course'],
    },
    content: [
      p(
        'Biblioteca de template-uri e un magazin intern de cursuri gata făcute. În loc să creezi un curs de la zero, alegi un template (de exemplu „Instructaj Introductiv General SSM" sau „Lucru la înălțime") și într-un singur click obții o copie completă — cu toate modulele, întrebările de verificare și testul final — pe care o personalizezi și o atribui angajaților.',
        'The template library is an internal store of ready-made courses. Instead of building a course from scratch, you pick a template (e.g. "General Introductory SSM Training" or "Working at height") and with one click get a complete copy — with all modules, check questions, and final test — that you customize and assign to employees.'
      ),
      h('Unde o găsești', 'Where to find it'),
      p(
        'Intră pe „Cursuri" din meniul stâng. În partea de sus a paginii apar două tab-uri: „Cursurile mele" și „📋 Biblioteca template-uri". Dacă vezi acest al doilea tab, ai permisiunea să folosești template-uri.',
        'Go to "Courses" from the left menu. At the top of the page you\'ll see two tabs: "My courses" and "📋 Template library". If you see this second tab, you have permission to use templates.'
      ),
      h('Ce conține biblioteca', 'What the library contains'),
      list(
        [
          'Template-uri oficiale de platformă — badge mov „🏛 Oficial platformă". Sunt livrate de noi la instalare și acoperă instruirile SSM/SU de bază cerute de legislația RO (HG 1425/2006, Legea 319/2006, HG 300/2006, OMAI 712/2005).',
          'Template-uri interne ale firmei tale SSM — badge verde „🏢 Firma ta SSM". Create de tine sau de colegii tăi instructori și disponibile tuturor instructorilor din firma SSM.',
        ],
        [
          'Platform official templates — "🏛 Official platform" badge. Shipped by us at install and cover basic SSM/SU trainings required by RO legislation.',
          'Internal templates of your SSM company — "🏢 Your SSM company" badge. Created by you or your fellow inspectors, available to all SSM inspectors in the firm.',
        ]
      ),
      h('Cum folosești un template', 'How to use a template'),
      steps(
        [
          'Click pe tab-ul „📋 Biblioteca template-uri" — vei vedea toate template-urile disponibile sub formă de carduri.',
          'Click pe cardul template-ului care te interesează, sau pe butonul „Vezi detalii" — se deschide pagina de preview cu toate modulele, textul narațiunilor, întrebările de verificare și testul final.',
          'Click pe butonul mare „✨ Folosește template-ul" — o fereastră de confirmare îți cere să-i dai titlu cursului nou (pre-populat cu un titlu sugerat).',
          'Confirmă. Se creează o copie completă a template-ului în firma ta, iar platforma te trimite direct la editare: detaliile cursului → conținut → poți adăuga video-uri, poți modifica texte, poți schimba scorul de promovare.',
          'Salvează. Cursul nou apare în „Cursurile mele" și îl poți atribui angajaților exact ca pe orice curs obișnuit.',
        ],
        [
          'Click the "📋 Template library" tab — you\'ll see all available templates as cards.',
          'Click the template card you want, or "View details" — a preview page opens with all modules, narration, check questions and final test.',
          'Click the big "✨ Use template" button — a confirm dialog asks for the new course title (pre-filled with a suggestion).',
          'Confirm. A full copy of the template is created in your company, and the platform takes you directly to editing.',
          'Save. The new course appears in "My courses" and can be assigned to employees like any other course.',
        ]
      ),
      tip(
        'Template-ul rămâne neschimbat oricât de mult îl folosești. Poți crea 100 de cursuri pornind de la același template — modificările fiecăruia nu afectează nici template-ul, nici cursurile create anterior.',
        "The template stays unchanged no matter how many times you use it. You can create 100 courses from the same template — changes to each don't affect the template or previously created courses."
      ),
      h('Cum creezi propriul tău template', 'How to create your own template'),
      steps(
        [
          'Deschide „📋 Biblioteca template-uri" și apasă butonul „Template nou" din dreapta sus (butonul „Instruire nouă" se transformă automat).',
          'Completează detaliile: titlu, tip instruire, durată, scor minim pentru test final. Checkbox-ul „📋 Publică acest curs ca template în biblioteca firmei mele" e bifat automat.',
          'Salvează. Ești redirecționat la editor de conținut — adaugă module, text, video, întrebări interactive și test final.',
          'Odată completat, template-ul apare automat în tab-ul „Biblioteca template-uri" pentru colegii tăi instructori. Tu îl poți edita oricând; modificările NU afectează cursurile deja clonate din el.',
        ],
        [
          'Open "📋 Template library" and press "New template" at the top right (the "New training" button transforms automatically).',
          'Fill in details: title, training type, duration, minimum passing score. The "📋 Publish this course as a template in my company library" checkbox is pre-checked.',
          "Save. You're redirected to the content editor — add modules, text, video, interactive questions, and final test.",
          'Once complete, the template automatically appears in the "Template library" tab for your fellow instructors. You can edit it anytime; changes do NOT affect courses already cloned from it.',
        ]
      ),
      tip(
        'Transformarea „curs existent → template": intri pe edit-ul oricărui curs al tău și bifezi „📋 Publică acest curs ca template". Util dacă ai deja un curs personalizat bine scris și vrei să-l faci disponibil ca punct de pornire pentru colegi.',
        'Transforming "existing course → template": edit any of your courses and check "📋 Publish this course as a template". Useful if you already have a well-written course and want to make it a starting point for colleagues.'
      ),
      h('Important de știut', 'Important to know'),
      list(
        [
          'Doar responsabilii SSM/PSI (rol SSM_COMPANY_ADMIN, SSM_INSTRUCTOR, sau flag-uri „Inspector SSM" / „Cadru tehnic PSI") pot vedea și folosi template-uri.',
          'Template-urile livrate de platformă sunt bazate pe legislația română la data creării. Verifică de fiecare dată să se alinieze cu ultima versiune a normelor și adaptează cu specificul firmei.',
          'Template-urile tale interne sunt vizibile doar instructorilor din firma ta SSM — nu se transmit automat firmelor client ale tale. Controlul rămâne la tine.',
          'Cursurile clonate dintr-un template sunt identice la momentul creării, apoi evoluează independent. Dacă updatezi template-ul mai târziu, cursurile existente NU se actualizează automat.',
        ],
        [
          'Only SSM/PSI responsibles (SSM_COMPANY_ADMIN, SSM_INSTRUCTOR roles, or "SSM Inspector" / "PSI Technical Officer" flags) can see and use templates.',
          'Platform-shipped templates are based on Romanian legislation at creation time. Always verify alignment with the latest regulations and adapt to your company specifics.',
          'Your internal templates are visible only to instructors in your SSM company — they do NOT automatically propagate to your client companies. Control stays with you.',
          'Courses cloned from a template are identical at creation, then evolve independently. If you update the template later, existing courses do NOT update automatically.',
        ]
      ),
    ],
  },
  {
    id: 'assign-courses',
    category: 'courses',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: { EMPLOYEE: ['managesDepartment'] },
    title: { ro: 'Atribuirea cursurilor', en: 'Assigning trainings' },
    content: [
      p(
        'Atribuirea este modul prin care legi un curs de unul sau mai mulți angajați — fără atribuire, cursul există doar în bibliotecă, dar nimeni nu-l poate parcurge. Accesezi fluxul din meniul „Atribuire cursuri" și treci prin câțiva pași ghidați.',
        'Assignment is how you link a course to one or more employees — without assignment, the course exists only in the library but no one can take it. Access the flow from the "Assign trainings" menu and go through a few guided steps.'
      ),
      h('Pașii de atribuire', 'Assignment steps'),
      steps(
        [
          'Alege cursul din listă — vezi și tipul și firma de care aparține.',
          'Selectează firma (dacă administrezi mai multe) sau departamentul.',
          'Alege angajații: individual, tot departamentul sau toată firma.',
          'Setează termenul-limită pentru finalizare, dacă este cazul.',
          'Confirmă — fiecare angajat primește o notificare în platformă și cursul apare imediat în „Cursurile lui".',
        ],
        [
          'Pick the course from the list — you also see its type and the owning company.',
          'Select the company (if you manage several) or the department.',
          'Choose the employees: individually, an entire department, or the whole company.',
          'Set a deadline for completion, if applicable.',
          'Confirm — each employee receives an in-app notification and the course shows up immediately in their list.',
        ]
      ),
      tip(
        'Ca COMPANY_ADMIN poți atribui doar cursuri din biblioteca firmei tale sau pe cele puse la dispoziție de firma SSM care te deservește. Nu poți atribui cursuri inițiale (IIG/ILM) — acestea sunt responsabilitatea firmei SSM.',
        "As COMPANY_ADMIN you can only assign courses from your company's library or those made available by the SSM company that serves you. You cannot assign initial courses (IIG/ILM) — those are the responsibility of the SSM company.",
        { onlyRoles: ['COMPANY_ADMIN'] }
      ),
      tip(
        'Ca CLM (Conducător Loc de Muncă), poți atribui cursuri de tip ILM, IP și Suplimentare doar angajaților din departamentul pe care îl conduci. Nu ai acces la angajați din alte departamente — intenționat, ca să nu intervii accidental în responsabilitatea altor CLM-uri.',
        "As a CLM (Workplace Leader), you can assign ILM, IP and Supplementary courses only to employees in the department you manage. You don't have access to employees from other departments — intentionally, so you don't accidentally step into another CLM's responsibility.",
        { onlyLabels: ['managesDepartment'] }
      ),
      tip(
        'Dacă un angajat are deja cursul respectiv atribuit și nefinalizat, platforma nu creează o atribuire duplicată — primești o notificare care îți spune cine a fost deja sărit.',
        'If an employee already has that course assigned and unfinished, the platform does not create a duplicate assignment — you get a notification telling you who was skipped.'
      ),
    ],
  },
  {
    id: 'course-stats',
    category: 'courses',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN'],
    title: { ro: 'Statistici per curs', en: 'Per-course statistics' },
    content: [
      p(
        'Pagina de statistici a unui curs îți arată cum performează cursul tău — câți au început, câți au promovat, la ce întrebări se blochează cursanții. Accesezi statisticile din lista de cursuri, apăsând pictograma de grafic pe cardul cursului.',
        'The course statistics page shows you how your course is performing — how many have started, how many have passed, and which questions trip learners up. You access stats from the course list by pressing the chart icon on the course card.'
      ),
      h('Ce rapoarte găsești', 'What reports you find'),
      list(
        [
          'Rata de finalizare: câți angajați au terminat cursul din totalul celor cărora le-a fost atribuit.',
          'Rata de promovare a testului final și scorul mediu obținut.',
          'Timpul mediu petrecut per modul — util pentru a identifica module prea lungi sau prea scurte.',
          'Lista angajaților cu statusul lor curent (Neînceput / În desfășurare / Promovat / Picat).',
          'Întrebările cu cele mai multe răspunsuri greșite — semn că tema nu e bine explicată în modul.',
        ],
        [
          'Completion rate: how many employees finished the course out of those it was assigned to.',
          'Final test pass rate and the average score.',
          'Average time spent per module — useful for spotting modules that are too long or too short.',
          'List of employees with their current status (Not started / In progress / Passed / Failed).',
          'Questions with the most wrong answers — a sign that the topic is not well explained in the module.',
        ]
      ),
      tip(
        'Dacă o întrebare are peste 50% răspunsuri greșite, verifică dacă tema este acoperită clar în modul și, dacă nu, reformulează întrebarea sau completează modulul. Statisticile sunt recalculate automat la fiecare intrare în pagină.',
        'If a question has over 50% wrong answers, check whether the topic is clearly covered in the module and, if not, rephrase the question or complete the module. Stats are recalculated automatically on each page visit.'
      ),
    ],
  },

  // ── ANGAJAȚI ───────────────────────────────────────────────────────────
  {
    id: 'employees-list',
    category: 'employees',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: { EMPLOYEE: ['canManageEmployees', 'managesDepartment'] },
    title: { ro: 'Lista de angajați', en: 'Employees list' },
    content: [
      p(
        'Lista de angajați este locul unde vezi toți oamenii pe care îi gestionezi — fie că este vorba de o firmă, un departament sau tot portofoliul tău de firme client. Accesezi pagina din fișa unei firme („Vezi angajații") sau direct din meniul „Angajați" acolo unde e disponibil.',
        'The employees list is where you see all the people you manage — whether it\'s one company, a department, or your entire client company portfolio. You access the page from a company detail view ("See employees") or directly from the "Employees" menu where available.'
      ),
      h('Ce poți face din listă', 'What you can do from the list'),
      list(
        [
          'Cauți un angajat după nume, email, funcție sau departament.',
          'Filtrezi după firmă, departament, funcție sau prezența EPI (echipament individual de protecție).',
          'Deschizi fișa unui angajat pentru a-i vedea atribuirile, certificatele și istoricul instruirilor.',
          'Editezi datele unui angajat sau îi setezi rolul și permisiunile.',
          'Ștergi un angajat (cu confirmare) — datele lui arhivate rămân pentru raportare, dar contul devine inactiv.',
        ],
        [
          'Search for an employee by name, email, job title or department.',
          'Filter by company, department, job title or EPI presence (personal protective equipment).',
          "Open an employee's profile to see their assignments, certificates and training history.",
          "Edit an employee's data or set their role and permissions.",
          'Delete an employee (with confirmation) — their archived data is kept for reporting, but the account becomes inactive.',
        ]
      ),
      h('Acțiuni în masă', 'Bulk actions'),
      p(
        'Pentru operațiuni repetitive folosește modul de selecție: activezi „Selectează" și bifezi mai mulți angajați simultan. Cea mai utilizată operațiune în masă este setarea intervalelor de Instruire Periodică (IP) — poți aplica același interval SSM și/sau SU pentru zeci de angajați dintr-un singur click.',
        'For repetitive operations use selection mode: enable "Select" and check multiple employees at once. The most common bulk action is setting Periodic Training (IP) intervals — you can apply the same SSM and/or SU interval to dozens of employees in a single click.'
      ),
      h('Tab-ul „IP scadente"', 'The "IP due" tab'),
      p(
        'Lista are și un tab special cu angajații care au Instruirea Periodică expirată sau aproape de expirare. Este pornită filtrată pe roșu și galben — exact cei pentru care trebuie să acționezi urgent. Din acest tab poți trimite și notificări de reminder (dacă rolul tău o permite).',
        'The list also has a special tab for employees whose Periodic Training is expired or near expiry. It opens filtered on red and yellow — exactly the ones who need urgent action. From this tab you can also send reminder notifications (if your role allows it).'
      ),
      tip(
        'Dacă administrezi mai multe firme client (SSM_COMPANY_ADMIN / SSM_INSTRUCTOR), pornește de la pagina „Firme" și alege firma — lista de angajați se deschide filtrată pe firma respectivă, ca să nu amesteci datele.',
        'If you manage multiple client companies (SSM_COMPANY_ADMIN / SSM_INSTRUCTOR), start from the "Companies" page and pick a company — the employees list opens filtered by that company, so you don\'t mix data.',
        { onlyRoles: ['SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'SUPER_ADMIN'] }
      ),
      tip(
        'Ca CLM vezi doar angajații din departamentul pe care îl conduci, nu pe toți cei ai firmei. Este filtrarea corectă — responsabilitatea ta se oprește la echipa ta.',
        'As a CLM you only see employees from the department you manage, not the whole company. This is the correct filter — your responsibility stops at your team.',
        { onlyLabels: ['managesDepartment'] }
      ),
    ],
  },
  {
    id: 'add-employee',
    category: 'employees',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: { EMPLOYEE: ['canManageEmployees'] },
    title: { ro: 'Adăugarea unui angajat', en: 'Adding an employee' },
    keywords: {
      ro: [
        'adauga angajat',
        'angajat nou',
        'importa excel',
        'bulk',
        'CLM',
        'HR',
        'cadru PSI',
        'EPI',
        'inspector SSM',
        'responsabilitati',
        'fisa angajat',
      ],
      en: [
        'add employee',
        'new employee',
        'import excel',
        'bulk',
        'CLM',
        'HR',
        'PSI officer',
        'EPI',
        'SSM inspector',
        'responsibilities',
        'employee profile',
      ],
    },
    content: [
      p(
        'Când adaugi un angajat în platformă, creezi pentru el un cont cu care se va autentifica, va parcurge cursurile atribuite și își va semna fișele. Odată ce salvezi, omul primește pe email datele de logare și instrucțiunile pentru prima conectare — nu trebuie să faci tu nimic manual suplimentar.',
        "When you add an employee to the platform, you create an account they will use to sign in, take assigned courses and sign their sheets. Once you save, the person receives their login details and first-connection instructions by email — you don't need to do anything manually after that."
      ),

      h(
        'Două metode: unul câte unul, sau mulți deodată din Excel',
        'Two methods: one by one, or many at once via Excel'
      ),
      p(
        'Pentru un angajat punctual (ex: o persoană nouă angajată săptămâna aceasta), folosește butonul „Adaugă angajat" și completezi formularul complet. Pentru zeci sau sute de angajați deodată (ex: onboarding-ul unei firme noi), folosește butonul „Importă Excel" — încarci un fișier cu toți angajații și platforma creează conturile în câteva secunde.',
        'For a single employee (e.g. a new hire this week), use the "Add employee" button and fill in the form. For tens or hundreds of employees at once (e.g. onboarding a new company), use the "Import Excel" button — you upload a file with all employees and the platform creates the accounts in seconds.'
      ),

      h('Formularul pentru un angajat individual', 'The individual employee form'),
      p(
        'Formularul are trei grupuri de câmpuri. Primele sunt obligatorii — fără ele nu poți salva. Celelalte sunt recomandate sau opționale — le completezi dacă ai informațiile la îndemână.',
        'The form has three groups of fields. The first ones are required — you cannot save without them. The others are recommended or optional — fill them in if you have the data at hand.'
      ),
      list(
        [
          'Obligatoriu: email, prenume, nume, funcția (meseria), firma din care face parte.',
          'Recomandat: departamentul, data angajării, numărul de telefon.',
          'Opțional (util pentru fișele oficiale): adresa de domiciliu, data și locul nașterii, numărul de marcă, grupa sanguină, calificarea, autorizații speciale pe care le deține.',
        ],
        [
          'Required: email, first name, last name, job title, company.',
          'Recommended: department, hire date, phone number.',
          'Optional (useful for official sheets): home address, birth date and place, badge number, blood group, qualification, special authorizations.',
        ]
      ),

      h(
        'Responsabilitățile speciale (bifele din partea de jos)',
        'Special responsibilities (checkboxes at the bottom)'
      ),
      p(
        'În partea de jos a formularului găsești 4 bife cu responsabilități speciale. Acestea NU sunt un rol de sine stătător — sunt o „insignă" pe care o atașezi unui angajat pentru a-i da drepturi sau pentru a-l marca cu o responsabilitate legală. Un angajat poate avea oricâte bife active în același timp.',
        'At the bottom of the form you find 4 checkboxes with special responsibilities. These are NOT standalone roles — they are a "badge" you attach to an employee to grant them rights or mark them with a legal responsibility. An employee can have any combination of these active at the same time.'
      ),
      list(
        [
          '„Poate gestiona angajați" — bifezi pentru HR-ul firmei sau pentru orice persoană care trebuie să adauge, editeze sau șteargă angajați. Fără această bifă, un angajat obișnuit nu vede nici măcar lista colegilor.',
          '„Inspector SSM" — bifezi pentru persoana care predă efectiv instruirile SSM în firmă. Numele ei va apărea automat pe fișele cursurilor ca instructor semnatar.',
          '„Cadru tehnic PSI" — bifezi pentru responsabilul cu apărarea împotriva incendiilor. El va semna automat fișele pentru cursurile de tip SU (Situații de Urgență). Fiecare firmă trebuie să aibă desemnat un cadru tehnic PSI.',
          '„Are EPI" — bifezi pentru angajații cărora li se oferă echipament individual de protecție (cască, mănuși, încălțăminte de protecție etc.). Bifa este pentru evidență — nu schimbă nimic în experiența angajatului, dar permite rapoarte și filtre rapide.',
        ],
        [
          '"Can manage employees" — check for the company HR or anyone who must add, edit or delete employees. Without this, a regular employee doesn\'t even see the colleague list.',
          '"SSM Inspector" — check for the person who actually teaches SSM trainings in the company. Their name will automatically appear on course sheets as the signing instructor.',
          '"PSI Technical Officer" — check for the fire-safety officer. They will automatically sign sheets for SU (Emergency Situations) courses. Every company must designate a PSI officer.',
          '"Has EPI" — check for employees issued personal protective equipment (helmet, gloves, safety footwear etc.). This is for records — it doesn\'t change the employee\'s experience but enables reports and filters.',
        ]
      ),
      p(
        'Pentru CLM (Conducător Loc de Muncă) nu există o bifă separată. Desemnarea se face diferit: mergi în „Departamente", alegi un departament și îl setezi pe acel angajat ca manager al departamentului. În acel moment el devine automat CLM pentru echipa respectivă — nu ai nimic de bifat aici.',
        'For CLM (Workplace Leader) there is no separate checkbox. You designate them differently: go to "Departments", pick a department, and set that employee as the department manager. At that moment they automatically become CLM for that team — nothing to check here.'
      ),

      h('Intervalele de Instruire Periodică (IP)', 'Periodic Training (IP) intervals'),
      p(
        'Pentru fiecare angajat poți stabili la câte zile se face reinstruirea SSM, respectiv SU. Valorile uzuale sunt 365 de zile (anual) sau 180 de zile (la 6 luni), dar le ajustezi în funcție de gradul de risc al postului. Dacă nu completezi nimic, angajatul moștenește intervalul firmei — nu rămâne fără scadență.',
        "For each employee you can set how many days pass between SSM and SU re-trainings. Typical values are 365 days (yearly) or 180 days (every 6 months), but you adjust based on the job risk level. If you leave this blank, the employee inherits the company default — they don't end up without a schedule."
      ),

      h('Importul în masă din Excel — pas cu pas', 'Bulk import from Excel — step by step'),
      steps(
        [
          'Apasă „Importă Excel" din pagina „Angajați". Se deschide un modal cu instrucțiuni.',
          'Descarcă șablonul oferit de platformă — are deja coloanele în ordinea corectă, cu exemple pe prima linie.',
          'Completează șablonul cu angajații tăi. Fiecare rând = un angajat. Nu modifica denumirile coloanelor și respectă formatul datei (zz.ll.aaaa sau aaaa-ll-zz).',
          'Salvează fișierul ca .xlsx (sau .csv) și încarcă-l în modal.',
          'Platforma validează fișierul ÎNAINTE de import — îți spune câte rânduri sunt corecte, câte au probleme și care este problema (ex: email duplicat, funcție lipsă).',
          'Dacă sunt rânduri cu probleme, corectează fișierul și reîncarcă-l. Dacă totul e OK, apasă „Confirmă import".',
          'Platforma creează conturile și trimite automat email de bun-venit fiecărui angajat nou.',
        ],
        [
          'Press "Import Excel" on the "Employees" page. A modal opens with instructions.',
          'Download the template provided by the platform — it already has the columns in the correct order, with examples on the first line.',
          "Fill the template with your employees. One row = one employee. Don't change column names and respect the date format (dd.mm.yyyy or yyyy-mm-dd).",
          'Save the file as .xlsx (or .csv) and upload it in the modal.',
          'The platform validates the file BEFORE importing — it tells you how many rows are correct, how many have problems, and what the problem is (e.g. duplicate email, missing job title).',
          'If there are problematic rows, fix the file and re-upload. If everything is OK, press "Confirm import".',
          'The platform creates the accounts and automatically sends a welcome email to each new employee.',
        ]
      ),
      tip(
        'Validarea preliminară e strictă în mod intenționat: niciun cont parțial nu este creat dacă fișierul are erori. Fie se creează toate rândurile curate, fie niciunul — așa că nu rămâi cu importuri „pe jumătate".',
        'The preliminary validation is strict on purpose: no partial accounts are created if the file has errors. Either all clean rows are created, or none — so you never end up with "half-imported" data.'
      ),
      tip(
        'Verifică de două ori adresa de email înainte de salvare: acolo pleacă parola inițială. Dacă angajatul nu are email încă, folosește provizoriu un email al firmei (ex: no-email+marius@firma.ro) și îl corectezi după ce primește adresa reală.',
        "Double-check the email address before saving: that's where the initial password goes. If the employee doesn't have an email yet, use a temporary company email (e.g. no-email+marius@company.ro) and correct it once they have a real one."
      ),
    ],
  },
  {
    id: 'ip-dashboard',
    category: 'employees',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: { EMPLOYEE: ['canManageEmployees', 'managesDepartment'] },
    title: { ro: 'Semaforizare Instruire Periodică', en: 'Periodic training traffic-light' },
    keywords: {
      ro: [
        'IP',
        'instruire periodica',
        'semafor',
        'expirare instruire',
        'la zi',
        'verde galben rosu',
        'dashboard IP',
        'scadente',
      ],
      en: [
        'IP',
        'periodic training',
        'traffic light',
        'training expiry',
        'up to date',
        'green yellow red',
        'IP dashboard',
        'due',
      ],
    },
    content: [
      p(
        'Instruirile Periodice (IP) trebuie repetate la intervale fixe stabilite prin lege sau prin regulamentul firmei — dacă se depășește termenul, angajatul nu mai este „la zi" și firma riscă sancțiuni la control. Dashboard-ul IP îți arată într-o singură privire starea tuturor angajaților tăi, folosind un sistem cu trei culori.',
        'Periodic Trainings (IP) must be repeated at fixed intervals set by law or by the company\'s regulation — if the deadline is missed, the employee is no longer "up to date" and the company risks penalties on inspection. The IP dashboard shows you the status of all your employees at a glance, using a three-color system.'
      ),
      h('Cele trei stări', 'The three states'),
      list(
        [
          'Verde — angajatul este la zi, instruirea viitoare este la distanță.',
          'Galben — expiră în curând (următoarele 30 de zile, de obicei). Moment bun pentru a atribui noua instruire.',
          'Roșu — instruirea a expirat sau angajatul nu a fost niciodată instruit. Acțiune urgentă.',
        ],
        [
          'Green — the employee is up to date, the next training is still some time away.',
          'Yellow — expires soon (typically within 30 days). A good moment to assign the new training.',
          'Red — the training has expired or the employee was never trained. Urgent action.',
        ]
      ),
      h('Filtre disponibile', 'Available filters'),
      list(
        [
          'Tipul instruirii: toate, doar SSM sau doar SU.',
          'Status: toate, doar verde, doar galben sau doar roșu.',
          'Căutare după angajat sau funcție.',
        ],
        [
          'Training type: all, SSM only, or SU only.',
          'Status: all, green only, yellow only, or red only.',
          'Search by employee or job title.',
        ]
      ),
      h('Trimiterea notificărilor de reminder', 'Sending reminder notifications'),
      p(
        'Din dashboard poți declanșa manual trimiterea notificărilor de reminder către toți angajații cu statut galben sau roșu — utile când ai configurat intervale noi și vrei să atenționezi rapid întreaga listă. Platforma mai trimite automat remindere la 30, 14 și 7 zile înainte de expirare, fără intervenția ta.',
        'From the dashboard you can manually trigger reminder notifications to all yellow or red employees — useful when you have configured new intervals and want to alert the full list quickly. The platform also sends automatic reminders 30, 14 and 7 days before expiry, without your intervention.',
        { onlyRoles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'] }
      ),
      tip(
        'Dacă vezi mulți angajați pe roșu pentru firma ta, atribuie instruirile direct din tab-ul „IP scadente" al listei de angajați — ai selecție în masă și aplici același interval la toți. Mult mai rapid decât unul câte unul.',
        'If you see many red employees in your company, assign trainings directly from the "IP due" tab of the employees list — you have bulk selection and apply the same interval to everyone. Much faster than one-by-one.'
      ),
    ],
  },

  // ── FIRME ──────────────────────────────────────────────────────────────
  {
    id: 'ssm-companies',
    category: 'companies',
    roles: ['SUPER_ADMIN'],
    title: { ro: 'Firme SSM', en: 'SSM companies' },
    content: [
      p(
        'Firmele SSM sunt furnizorii de servicii de securitate și sănătate în muncă care folosesc platforma pentru a-și deservi portofoliul de clienți. Fiecare firmă SSM își are propria echipă (administratori și instructori) și propria listă de firme-client pe care le instruiește. Ca SUPER_ADMIN, tu administrezi toate firmele SSM din platformă.',
        'SSM companies are the occupational health and safety service providers that use the platform to serve their client portfolio. Each SSM company has its own team (administrators and instructors) and its own list of client companies it trains. As SUPER_ADMIN, you administer all SSM companies on the platform.'
      ),
      h('Ce poți face', 'What you can do'),
      list(
        [
          'Vezi lista tuturor firmelor SSM cu numărul de firme-client asociate fiecăreia.',
          'Extinzi o firmă SSM pentru a vedea toate firmele ei client într-o arborescență.',
          'Adaugi o firmă SSM nouă — CUI, denumire, date de contact, administrator inițial.',
          'Dezactivezi o firmă SSM (păstrează datele, blochează accesul) sau o ștergi definitiv (cu confirmare explicită a numelui, ca dublă siguranță).',
        ],
        [
          'See all SSM companies with the number of client companies associated to each.',
          'Expand an SSM company to see all its client companies in a tree.',
          'Add a new SSM company — VAT code, name, contact details, initial administrator.',
          'Deactivate an SSM company (keeps data, blocks access) or delete it permanently (requires typing the name for safety).',
        ]
      ),
      tip(
        'Fă deosebirea între „Dezactivează" și „Șterge": dezactivarea e reversibilă și e recomandată pentru pauze temporare (neplată, reorganizare). Ștergerea e definitivă — toate firmele-client, angajații, cursurile și fișele se pierd.',
        'Distinguish between "Deactivate" and "Delete": deactivation is reversible and recommended for temporary pauses (non-payment, reorganization). Deletion is permanent — all client companies, employees, courses and sheets are lost.'
      ),
    ],
  },
  {
    id: 'client-companies',
    category: 'companies',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'],
    title: { ro: 'Firme client', en: 'Client companies' },
    content: [
      p(
        'Firmele client sunt companiile pe care tu (ca firmă SSM) le instruiești. Fiecare firmă client are propriul administrator, propriii angajați și propriile cursuri atribuite. Lista firmelor client este punctul de pornire pentru majoritatea operațiunilor tale zilnice — de aici intri la angajați, documente, fișe și statistici.',
        'Client companies are the companies you (as an SSM company) train. Each client company has its own administrator, employees, and assigned courses. The client companies list is the starting point for most of your daily operations — from here you access employees, documents, sheets and stats.'
      ),
      h('Ce vezi pe fiecare card', 'What you see on each card'),
      list(
        [
          'Numele firmei și CUI-ul.',
          'Numărul de angajați înregistrați și procentul celor cu instruire la zi.',
          'Starea semaforizării IP pe firmă (cât verde, galben, roșu).',
          'Acțiuni rapide: Vezi angajații, Vezi documentele, Vezi fișele, Editează.',
        ],
        [
          'Company name and VAT code.',
          'Number of registered employees and percentage of those up to date.',
          'Company-level IP traffic-light state (green / yellow / red counts).',
          'Quick actions: See employees, See documents, See sheets, Edit.',
        ]
      ),
      h('Adăugarea unei firme client noi', 'Adding a new client company'),
      steps(
        [
          'Apasă „Firmă nouă" din pagina „Firme client".',
          'Completează datele: denumire, CUI, adresa sediului, punct de lucru, număr de angajați estimat.',
          'Atribuie firma unei firme SSM părinte (dacă ești SUPER_ADMIN) — pentru SSM_COMPANY_ADMIN se completează automat cu firma ta.',
          'Creează contul administratorului firmei client (COMPANY_ADMIN) — emailul lui primește datele de autentificare.',
          'Salvează. Firma apare imediat în listă și poți începe să-i adaugi angajați.',
        ],
        [
          'Press "New company" on the "Client companies" page.',
          'Fill in the details: name, VAT code, headquarters address, work point, estimated employee count.',
          "Assign the company to a parent SSM company (if you are SUPER_ADMIN) — for SSM_COMPANY_ADMIN it's auto-filled with your company.",
          'Create the client company administrator account (COMPANY_ADMIN) — their email receives the credentials.',
          'Save. The company appears immediately in the list and you can start adding employees.',
        ]
      ),
      tip(
        'Ca SSM_INSTRUCTOR vezi doar firmele client pe care instructorul tău părinte (SSM_COMPANY_ADMIN) ți le-a distribuit. Dacă lipsește o firmă pe care ar trebui să o instruiești, cere-i administratorului să ți-o aloce.',
        'As SSM_INSTRUCTOR you only see client companies that your parent administrator (SSM_COMPANY_ADMIN) has assigned to you. If a company you should be training is missing, ask your administrator to assign it to you.',
        { onlyRoles: ['SSM_INSTRUCTOR'] }
      ),
    ],
  },
  {
    id: 'my-company',
    category: 'companies',
    roles: ['COMPANY_ADMIN', 'SSM_COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: { EMPLOYEE: ['canManageEmployees'] },
    title: { ro: 'Firma mea (pagina cu toate tab-urile)', en: 'My company (all tabs page)' },
    keywords: {
      ro: ['firma mea', 'firma mea SSM', 'pagina firmei', 'tab-uri firma', 'date firma'],
      en: ['my company', 'my SSM company', 'company page', 'company tabs'],
    },
    content: [
      p(
        'Pagina firmei este centrul în care găsești tot ce ține de compania ta. Se deschide pe mai multe tab-uri, fiecare cu scopul lui: vezi date generale, angajați, departamente, documente, fișe și rapoarte. Accesezi pagina din meniul din stânga apăsând „Firma mea" (sau „Firma mea SSM" dacă ești la o firmă SSM).',
        'The company page is the hub where you find everything related to your company. It opens on multiple tabs, each with its own purpose: you see general data, employees, departments, documents, sheets and reports. Access it from the left menu by pressing "My company" (or "My SSM company" if you are at an SSM company).'
      ),
      h('Tab-urile paginii', 'The page tabs'),
      list(
        [
          'Date generale — denumirea firmei, CUI-ul, adresele, firma SSM care o deservește (pentru firmele client), serviciile active.',
          'Angajați — lista completă a angajaților firmei, cu statusul lor de instruire.',
          'Departamente — sub-diviziunile firmei și managerii lor (CLM-urile).',
          'Documente — dosarul cu documente încărcate manual (evaluare riscuri, contract SSM, decizii etc.).',
          'Fișe — fișele SSM generate din cursuri, cu statusul de semnare.',
          'Raport ITM — raportul pentru inspecție, cu preview live și rapoarte oficiale semnate.',
        ],
        [
          'General data — company name, VAT code, addresses, SSM company serving it (for client companies), active services.',
          'Employees — full list of company employees, with their training status.',
          'Departments — company subdivisions and their managers (CLMs).',
          'Documents — folder with manually uploaded documents (risk assessment, SSM contract, decisions etc.).',
          'Sheets — SSM sheets generated from courses, with signing status.',
          'ITM Report — inspection report, with live preview and signed official reports.',
        ]
      ),
      tip(
        'Ca administrator de firmă client (COMPANY_ADMIN) poți modifica datele firmei și ai acces complet la toate tab-urile. Fiecare tab are propria secțiune în acest manual — consultă-le pentru detalii.',
        'As a client company administrator (COMPANY_ADMIN) you can modify company data and have full access to all tabs. Each tab has its own section in this manual — consult them for details.',
        { onlyRoles: ['COMPANY_ADMIN'] }
      ),
      tip(
        'Ca administrator al firmei SSM, „Firma mea SSM" este contextul tău de bază — aici vezi și gestionezi echipa ta (instructorii tăi), documentele firmei SSM și rapoartele pe propria firmă.',
        'As an SSM company administrator, "My SSM company" is your base context — here you see and manage your team (your instructors), the SSM company documents, and reports on your own company.',
        { onlyRoles: ['SSM_COMPANY_ADMIN'] }
      ),
      tip(
        'Ca HR al firmei, poți gestiona angajații și departamentele, dar modificarea datelor firmei (denumire, CUI, adrese) rămâne doar în sarcina administratorului. Dacă observi o eroare în datele firmei, semnalează-i.',
        "As company HR, you can manage employees and departments, but changing company data (name, VAT code, addresses) remains the administrator's responsibility. If you notice an error in company data, flag it to them.",
        { onlyRoles: ['EMPLOYEE'] }
      ),
    ],
  },
  {
    id: 'departments',
    category: 'companies',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: { EMPLOYEE: ['canManageEmployees', 'managesDepartment'] },
    title: { ro: 'Departamente', en: 'Departments' },
    content: [
      p(
        'Departamentele sunt subdiviziunile unei firme (ex: Producție, Administrativ, Logistică). Le folosești pentru a organiza angajații, a atribui cursuri în masă pe grup de angajați cu risc similar și pentru a desemna un manager de departament responsabil de instruirea echipei sale.',
        "Departments are the subdivisions of a company (e.g. Production, Administration, Logistics). You use them to organize employees, assign courses in bulk to groups of employees with similar risk, and to appoint a department manager responsible for the team's training."
      ),
      h('Ce poți face cu un departament', 'What you can do with a department'),
      list(
        [
          'Creezi un departament nou specificând numele, tipul (SSM / PSI / general) și managerul.',
          'Editezi numele sau schimbi managerul.',
          'Ștergi un departament gol (dacă are angajați, trebuie mutați întâi).',
          'Importanți angajații în bloc dintr-un fișier Excel, direct în departamentul curent.',
          'Atribui un curs tuturor angajaților unui departament dintr-un singur click.',
        ],
        [
          'Create a new department specifying name, type (SSM / PSI / general), and manager.',
          'Edit the name or change the manager.',
          'Delete an empty department (if it has employees, they must be moved first).',
          'Bulk-import employees from an Excel file straight into the current department.',
          'Assign a course to all employees in a department in a single click.',
        ]
      ),
      h('Managerul de departament', 'The department manager'),
      p(
        'Fiecare departament poate avea un manager desemnat — un angajat obișnuit care primește permisiunea de a gestiona colegii din departamentul său (vede statusul lor de instruire, poate atribui cursuri, primește notificări când cineva are IP expirat). Astfel, responsabilitatea instruirii se distribuie natural pe echipe, fără să fie nevoie ca toți angajații-cheie să aibă rol de administrator.',
        'Each department can have an appointed manager — a regular employee who receives permission to manage their department colleagues (sees their training status, can assign courses, receives notifications when someone has expired IP). This way, training responsibility is naturally distributed across teams, without making every key employee an administrator.'
      ),
      tip(
        'Ca angajat desemnat manager de departament, vei vedea în meniul „Management" opțiuni suplimentare pentru colegii tăi — chiar dacă rolul tău rămâne EMPLOYEE. Semnalează administratorului firmei dacă ești responsabil de fapt de o echipă, dar nu ți-a fost setat flagul.',
        'As an employee appointed as department manager, you will see additional "Management" options in the menu for your colleagues — even though your role remains EMPLOYEE. Report to the company administrator if you are effectively responsible for a team but the flag hasn\'t been set for you.',
        { onlyRoles: ['EMPLOYEE'] }
      ),
    ],
  },
  {
    id: 'itm-report',
    category: 'companies',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN'],
    title: { ro: 'Raportul pentru inspecție ITM', en: 'ITM inspection report' },
    keywords: {
      ro: [
        'ITM',
        'inspector teritorial de munca',
        'raport inspectie',
        'control ITM',
        'dosar ITM',
        'compliance',
        'raport oficial',
        'preview raport',
      ],
      en: [
        'ITM',
        'labor inspection',
        'inspection report',
        'ITM control',
        'ITM dossier',
        'compliance',
        'official report',
      ],
    },
    content: [
      p(
        'Raportul ITM este documentul pe care îl prezinți la Inspecția Teritorială de Muncă (ITM) pentru a demonstra că firma ta are situația SSM/SU în regulă. Platforma îl generează automat pe baza datelor reale din firmă — nu trebuie să-l faci manual în Word sau Excel. Ajungi la el intrând pe pagina firmei și apăsând tab-ul „Raport ITM".',
        'The ITM report is the document you present to the Territorial Labor Inspectorate (ITM) to prove your company\'s OHS/SU status is in order. The platform generates it automatically from the real company data — you don\'t need to build it manually in Word or Excel. Access it by opening the company page and pressing the "ITM Report" tab.'
      ),
      h('Ce conține raportul', 'What the report contains'),
      list(
        [
          'Datele firmei: denumire, CUI, adresă, servicii active, tipul de aranjament SSM.',
          'Personalul cheie: instructorul SSM, cadrul tehnic PSI, administratorul firmei, CLM-urile.',
          'Un tabel cu fiecare angajat și starea lui de compliance (cursuri la zi, scadențe, lipsuri).',
          'O sinteză agregată (câți la zi, câți cu expirare apropiată, câți restanți).',
          '2 sloturi de semnătură: administratorul firmei + responsabilul SSM.',
        ],
        [
          'Company data: name, VAT code, address, active services, type of SSM arrangement.',
          'Key personnel: SSM inspector, PSI technical officer, company administrator, CLMs.',
          'A table with each employee and their compliance state (up-to-date courses, deadlines, gaps).',
          'An aggregated summary (how many up to date, near expiry, overdue).',
          '2 signature slots: company administrator + SSM officer.',
        ]
      ),
      h(
        'Două variante: preview live și raport oficial',
        'Two versions: live preview and official report'
      ),
      p(
        '„Preview live" este un PDF nesemnat pe care îl poți descărca oricând, pentru tine, ca să vezi cum stă firma în momentul acesta. Se regenerează la fiecare descărcare cu datele curente — deci nu e un document oficial, dar e util pentru verificări rapide înainte de control.',
        '"Live preview" is an unsigned PDF you can download anytime, for yourself, to see the company\'s current state. It regenerates on every download with the current data — not an official document, but useful for quick checks before an inspection.'
      ),
      p(
        '„Raport oficial" este un snapshot semnat. Când apeși „Generează raport oficial", platforma îngheață datele la acel moment și trimite fișierul la cele 2 semnături (administrator și responsabil SSM). După ce ambii semnează, documentul devine raport oficial arhivat, pe care îl poți prezenta la ITM.',
        '"Official report" is a signed snapshot. When you press "Generate official report", the platform freezes the data at that moment and sends the file for the 2 signatures (administrator and SSM officer). After both sign, the document becomes an archived official report you can present to ITM.'
      ),
      h('Dosarul complet (ZIP)', 'Full dossier (ZIP)'),
      p(
        'Pentru inspecții mai serioase, platforma îți generează și un dosar ZIP care conține raportul oficial + fișele individuale de instruire semnate + documentele suport încărcate la „Documente" (evaluarea riscurilor, contractul SSM, registrul unic, planul anual, deciziile interne, certificatele instructorilor, fișele EIP, autorizațiile). Descarci un singur ZIP și ai tot dosarul pregătit pentru prezentare.',
        'For more serious inspections, the platform also generates a ZIP dossier containing the official report + signed individual training sheets + support documents uploaded under "Documents" (risk assessment, SSM contract, single register, annual plan, internal decisions, instructor certificates, PPE sheets, authorizations). You download one ZIP and have the entire dossier ready for presentation.'
      ),
      h('Fluxul tipic înainte de un control', 'Typical flow before an inspection'),
      steps(
        [
          'Descarcă „Preview live" și verifică ce lipsește (angajați fără IP la zi, fișe nesemnate, documente suport lipsă).',
          'Rezolvă problemele: atribuie instruirile restanțe, încarcă documentele lipsă la „Documente".',
          'Apasă „Generează raport oficial" — platforma îngheață datele și pornește semnarea.',
          'Semnează ca administrator (sau cere responsabilului SSM să semneze primul, în funcție de fluxul firmei).',
          'Descarcă „Dosarul complet (ZIP)" cu raportul oficial semnat + toate anexele.',
          'Prezinți ZIP-ul la control, extragi ce cere inspectorul.',
        ],
        [
          'Download "Live preview" and check what\'s missing (employees without up-to-date IP, unsigned sheets, missing support documents).',
          'Fix the issues: assign overdue trainings, upload missing documents under "Documents".',
          'Press "Generate official report" — the platform freezes the data and starts signing.',
          "Sign as administrator (or ask the SSM officer to sign first, depending on your company's flow).",
          'Download "Full dossier (ZIP)" with the signed official report + all attachments.',
          'Present the ZIP at the inspection, extract what the inspector asks for.',
        ]
      ),
      tip(
        'Ca să apară documentele suport în ZIP, asigură-te că le încarci la tab-ul „Documente" cu categoriile corecte (ex: „Evaluare riscuri", „Contract SSM"). Dacă categoria nu e setată, documentul nu e asociat automat cu raportul.',
        'For support documents to appear in the ZIP, make sure you upload them to the "Documents" tab with the correct categories (e.g. "Risk assessment", "SSM contract"). If the category is not set, the document is not automatically associated with the report.'
      ),
      tip(
        'Rapoartele oficiale nu pot fi șterse după semnare — sunt dovada istorică a situației firmei la o dată fixă. Dacă ai greșit, poți șterge un raport DOAR cât timp nu are nicio semnătură aplicată.',
        "Official reports cannot be deleted after signing — they are the historical proof of the company's state at a fixed date. If you made a mistake, you can delete a report ONLY while it has no signatures yet."
      ),
    ],
  },
  {
    id: 'company-documents',
    category: 'companies',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN'],
    title: { ro: 'Documentele firmei', en: 'Company documents' },
    content: [
      p(
        'Fiecare firmă are un dosar de documente unde se păstrează tot ceea ce ține de activitatea SSM: procese-verbale, decizii, fișe de aptitudine medicală, instrucțiuni proprii, fișe de post, autorizații, note interne. Platforma organizează aceste documente într-un spațiu central, accesibil tuturor celor care au rol în firma respectivă.',
        'Each company has a document folder holding everything related to OHS activity: minutes, decisions, medical fitness assessments, in-house instructions, job descriptions, authorizations, internal notes. The platform organizes these documents in a central space accessible to everyone with a role in that company.'
      ),
      h('Ce poți face', 'What you can do'),
      list(
        [
          'Încarci unul sau mai multe documente deodată (PDF, Word, Excel, imagini, text).',
          'Alegi un tip de document dintr-un dropdown — dacă tipul de care ai nevoie nu există, îl scrii și se adaugă automat în lista firmei tale (ex: „Fișa postului", „Autorizație ISCIR").',
          'Bifezi „Include în raportul ITM" dacă documentul trebuie să apară în dosarul oficial pentru inspecție.',
          'Bifezi „Atribuie unuia sau mai multor angajați" și îi cauți după nume, funcție sau departament; documentul va apărea în dosarul fiecăruia dintre ei.',
          'Folosești secțiunea „Vezi documente" cu filtrele (angajat / tip / doar cele din raport ITM) ca să găsești rapid ce te interesează.',
          'Descarci orice document — se salvează local cu un nume curat (Nume_Prenume_Tip_Data.pdf).',
          'Ștergi un document fără griji — fișierul fizic rămâne pe server atâta timp cât alți angajați mai au documentul atribuit, și se elimină automat când nu mai e referit de nimeni.',
        ],
        [
          'Upload one or several documents at once (PDF, Word, Excel, images, text).',
          'Pick a document type from a dropdown — if the type you need does not exist, type it and it is added to your company list (e.g. "Job description", "ISCIR authorization").',
          'Tick "Include in ITM report" if the document should appear in the official inspection dossier.',
          'Tick "Assign to one or more employees" and search by name, job title or department; the document will show up in each of their dossiers.',
          'Use the "View documents" area with its filters (employee / type / ITM-only) to quickly find what you need.',
          'Download any document — it is saved locally under a clean name (FirstName_LastName_Type_Date.pdf).',
          'Delete a document safely — the physical file stays on the server as long as other employees still have the document assigned, and is removed automatically when no one references it anymore.',
        ]
      ),
      h('Cum sunt stocate', 'How they are stored'),
      p(
        'Fișierele fizice sunt ținute o singură dată, identificate prin amprenta digitală a conținutului — dacă atribui același document la 50 de angajați, pe disc există tot un singur fișier, iar în platformă apare la fiecare dintre ei. Metadatele (tip, descriere, cine, când, pentru cine, bifa ITM) sunt păstrate în baza de date, nu în numele fișierului. Asta înseamnă încărcare instantanee chiar și când atribui unui număr mare de angajați.',
        "Physical files are kept once, identified by the digital fingerprint of their content — if you assign the same document to 50 employees, there is still a single file on disk, and it appears on each employee's record in the app. Metadata (type, description, who, when, for whom, ITM flag) is stored in the database, not in the file name. This means instant upload even when assigning to a large number of employees."
      ),
      h('Raportul ITM', 'ITM report'),
      p(
        'Bifa „Include în raportul ITM" decide dacă documentul apare în dosarul ZIP de inspecție. Documentele la nivel de firmă (fără angajat atribuit) intră în folderul „01_Documente_firma/", iar cele atribuite unui angajat intră în „03_Documente_angajati/<Nume_Prenume>/". Fișele de instruire generate din cursuri rămân în „02_Fise_instruire/".',
        'The "Include in ITM report" checkbox decides whether the document appears in the inspection ZIP dossier. Company-level documents (with no employee assigned) go into the "01_Documente_firma/" folder, and those assigned to an employee go into "03_Documente_angajati/<FirstName_LastName>/". Training sheets generated from courses stay in "02_Fise_instruire/".'
      ),
      tip(
        'Folderul de documente este separat de fișele SSM — fișele se generează automat din cursuri și se găsesc în pagina „Fișe". Documentele sunt pentru materiale externe, încărcate manual (ex: avizele de la medicina muncii, fișa postului, autorizațiile profesionale).',
        'The document folder is separate from SSM sheets — sheets are generated automatically from courses and live on the "Sheets" page. Documents are for external materials uploaded manually (e.g. occupational medicine approvals, job descriptions, professional authorizations).'
      ),
    ],
  },

  // ── FIȘE SSM ───────────────────────────────────────────────────────────
  {
    id: 'sheets-overview',
    category: 'sheets',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: {
      EMPLOYEE: ['managesDepartment', 'isSSMInstructor', 'isCadruTehnicPSI'],
    },
    title: { ro: 'Despre fișele SSM', en: 'About SSM sheets' },
    keywords: {
      ro: [
        'fișă individuală',
        'fișă colectivă',
        'fișă de traseu',
        'PDF fișă',
        'semnare fișe',
        'generare fișe',
        'arhivare fișe',
      ],
      en: [
        'individual sheet',
        'collective sheet',
        'route sheet',
        'PDF sheet',
        'sign sheets',
        'generate sheets',
        'archive sheets',
      ],
    },
    content: [
      p(
        'Fișele SSM sunt documentele legale care dovedesc faptul că instruirea a avut loc și că participanții au fost instruiți. Până acum acestea erau tipărite și semnate olograf; platforma le generează automat ca PDF și colectează semnăturile digitale ale tuturor părților implicate: cursant, instructor, responsabil SSM, admitere la lucru.',
        'SSM sheets are the legal documents proving that the training took place and that the participants were trained. In the past these were printed and signed by hand; the platform generates them automatically as PDF and collects the digital signatures of all parties involved: trainee, instructor, SSM officer, admission to work.'
      ),
      h('Tipuri de fișe', 'Types of sheets'),
      list(
        [
          'Fișa individuală de instruire — una per cursant per curs, cu toate semnăturile necesare.',
          'Fișa colectivă — grupează mai mulți cursanți pe același document, utilă pentru instruiri desfășurate simultan cu o echipă.',
          'Fișa de traseu — documentează drumul casă–muncă al angajatului, generată din datele declarate de el.',
        ],
        [
          'Individual training sheet — one per trainee per course, with all required signatures.',
          'Collective sheet — groups multiple trainees on the same document, useful for trainings held simultaneously with a team.',
          "Route sheet — documents the employee's home–work commute, generated from the data they declared.",
        ]
      ),
      h('Fluxul unei fișe', 'The lifecycle of a sheet'),
      steps(
        [
          'Cursul este finalizat de cursant (toate modulele parcurse, test promovat).',
          'Platforma generează automat fișa PDF cu datele cursantului, cursul, data, conținutul.',
          'Cursantul semnează fișa digital.',
          'Responsabilii de proces (instructor, responsabil SSM, persoana care admite la lucru) primesc fiecare o notificare și semnează pe rând.',
          'După ultima semnătură, fișa este arhivată ca document oficial și este disponibilă pentru descărcare.',
        ],
        [
          'The course is completed by the trainee (all modules watched, test passed).',
          'The platform automatically generates the PDF sheet with trainee data, course, date, content.',
          'The trainee signs the sheet digitally.',
          'The process signatories (instructor, SSM officer, person admitting to work) each receive a notification and sign in turn.',
          'After the last signature, the sheet is archived as an official document and is available for download.',
        ]
      ),
      tip(
        'Fișele semnate nu pot fi modificate retroactiv — semnătura lor este „sigiliul" legal. Dacă ai făcut o greșeală înainte de semnare (ex: un câmp greșit), contactează responsabilul SSM pentru a anula fișa și a genera una nouă.',
        'Signed sheets cannot be modified retroactively — their signature is the legal "seal". If you made a mistake before signing (e.g. a wrong field), contact the SSM officer to cancel the sheet and generate a new one.'
      ),
    ],
  },
  {
    id: 'sheets-to-sign',
    category: 'sheets',
    roles: ['SSM_INSTRUCTOR', 'COMPANY_ADMIN', 'SSM_COMPANY_ADMIN', 'EMPLOYEE'],
    requireLabelsForRoles: {
      EMPLOYEE: ['managesDepartment', 'isSSMInstructor', 'isCadruTehnicPSI'],
    },
    title: { ro: 'Fișe de semnat', en: 'Sheets to sign' },
    keywords: {
      ro: [
        'semnare fișe',
        'semnare fise',
        'semnez fișă',
        'semnare documente',
        'aprob fișă',
        'admit la lucru',
        'semnătură electronică pe fișe',
      ],
      en: [
        'sign sheet',
        'sign sheets',
        'approve sheet',
        'admit to work',
        'electronic signature on sheets',
      ],
    },
    content: [
      p(
        'Pagina „Fișe de semnat" îți arată toate fișele pe care trebuie să le semnezi tu — fie pentru că ești instructorul care a predat cursul, fie pentru că ești responsabil de admitere la lucru, fie pentru că ești managerul unui departament. Când un cursant termină un curs și semnează, fișa apare automat aici pentru tine.',
        'The "Sheets to sign" page lists all the sheets you need to sign — either because you are the instructor who taught the course, or the person admitting to work, or a department manager. When a trainee completes a course and signs, the sheet shows up here for you automatically.'
      ),
      h('Cum semnezi', 'How to sign'),
      steps(
        [
          'Deschide pagina „Fișe de semnat" — vezi lista cu toate fișele în așteptare.',
          'Filtrează după firmă, departament sau funcție dacă lista este lungă.',
          'Apasă pe o fișă pentru a vedea conținutul PDF înainte de semnare.',
          'Apasă „Semnează" — platforma aplică automat semnătura ta digitală configurată în profil.',
          'Fișa trece la următorul semnatar. Când toți au semnat, este arhivată ca document final.',
        ],
        [
          'Open the "Sheets to sign" page — you see the list of all pending sheets.',
          'Filter by company, department or job title if the list is long.',
          'Press on a sheet to see the PDF content before signing.',
          'Press "Sign" — the platform automatically applies the digital signature configured in your profile.',
          'The sheet moves to the next signatory. Once everyone has signed, it is archived as the final document.',
        ]
      ),
      h('Semnarea în masă', 'Bulk signing'),
      p(
        'Dacă ai de semnat multe fișe similare (ex: o instruire colectivă la care au participat 30 de oameni), folosește modul „Selectează multiple" — bifezi fișele pe care le accepți și semnezi într-un singur pas. Utilizarea semnării în masă este recomandată doar după ce ai verificat conținutul; răspunderea pentru semnătură rămâne a ta.',
        'If you have many similar sheets to sign (e.g. a collective training attended by 30 people), use the "Select multiple" mode — tick the sheets you accept and sign in one step. Bulk signing is recommended only after you have verified the content; responsibility for your signature remains yours.'
      ),
      tip(
        'Ca CLM semnezi fișele colegilor din echipa ta în calitate de admitere la lucru. Ca Inspector SSM semnezi ca instructor pe fișele cursurilor pe care le predai. Ca Cadru PSI semnezi fișele cursurilor SU. Toate aceste roluri pot coexista pe același cont — vei vedea pur și simplu mai multe fișe în listă.',
        "As a CLM you sign your team colleagues' sheets in the admission-to-work capacity. As an SSM Inspector you sign as instructor on sheets for courses you teach. As a PSI Officer you sign SU course sheets. All these roles can coexist on the same account — you will simply see more sheets in the list.",
        { onlyRoles: ['EMPLOYEE'] }
      ),
      tip(
        'Notificările despre fișe noi de semnat apar în clopoțel (din dreapta sus). Poți activa și reminderele zilnice pentru ele — util dacă ești pe teren și verifici rar lista.',
        'Notifications about new sheets to sign appear in the bell icon (top-right). You can also enable daily reminders for them — useful if you work in the field and rarely check the list.'
      ),
    ],
  },

  // ── RAPOARTE & NOTIFICĂRI ──────────────────────────────────────────────
  {
    id: 'notifications',
    category: 'reports',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR', 'COMPANY_ADMIN'],
    title: { ro: 'Notificări', en: 'Notifications' },
    content: [
      p(
        'Notificările sunt modul prin care platforma îți atrage atenția asupra lucrurilor importante fără să fie nevoie să verifici toate paginile manual: o fișă în așteptarea semnăturii tale, un angajat cu instruire expirată, un curs atribuit unui coleg sau un reminder de IP apropiat de termen. Le vezi în două locuri: clopoțelul din bara de sus (vezi ultimele notificări și contorul de necitite) și pagina „Notificări" (istoricul complet cu filtre).',
        'Notifications are how the platform brings important items to your attention without making you check every page manually: a sheet awaiting your signature, an employee with expired training, a course assigned to a colleague, or an upcoming IP reminder. You see them in two places: the bell icon in the top bar (last notifications and unread counter) and the "Notifications" page (full history with filters).'
      ),
      h('Tipuri de notificări primite', 'Types of notifications you receive'),
      list(
        [
          'Curs atribuit — cineva ți-a atribuit un curs pe care trebuie să-l parcurgi.',
          'Curs finalizat — unul dintre angajații tăi a promovat un curs.',
          'Fișă de semnat — o fișă este în așteptarea semnăturii tale.',
          'Instruire Periodică expiră — angajat cu IP apropiat de termen sau expirat.',
          'Mesaje de sistem — modificări de permisiuni, reset parolă, alte notificări administrative.',
        ],
        [
          'Course assigned — someone assigned you a course to take.',
          'Course completed — one of your employees passed a course.',
          'Sheet to sign — a sheet is awaiting your signature.',
          'Periodic Training expiring — employee with IP near deadline or expired.',
          'System messages — permission changes, password resets, other administrative notifications.',
        ]
      ),
      h('Gestionarea notificărilor', 'Managing notifications'),
      list(
        [
          'Apasă pe o notificare pentru a merge direct la subiectul ei (ex: fișa de semnat, pagina angajatului).',
          'Marchează notificările ca citite individual sau pe toate odată.',
          'Filtrează după tip sau după status (citită / necitită).',
          'Poți dezactiva reminderele IP din profilul tău, dacă nu vrei să le primești.',
        ],
        [
          'Click a notification to jump straight to its subject (e.g. the sheet to sign, the employee page).',
          'Mark notifications as read individually or all at once.',
          'Filter by type or status (read / unread).',
          "You can disable IP reminders from your profile if you don't want to receive them.",
        ]
      ),
      tip(
        'Reminderele IP automate se trimit cu 30, 14 și 7 zile înainte de expirare, plus la expirare și săptămânal după. Dacă nu dezactivezi funcția, nu e nevoie să trimiți manual nimic — platforma atenționează responsabilii la momentele potrivite.',
        "Automatic IP reminders are sent 30, 14 and 7 days before expiry, plus on expiry and weekly after. Unless you disable the feature, you don't need to trigger anything manually — the platform alerts the responsible parties at the right moments."
      ),
    ],
  },
  {
    id: 'global-reports',
    category: 'reports',
    roles: ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN'],
    title: { ro: 'Rapoarte globale', en: 'Global reports' },
    content: [
      p(
        'Rapoartele globale îți oferă o privire de ansamblu asupra activității din tot portofoliul tău (pentru SSM_COMPANY_ADMIN, firmele client pe care le deservești; pentru SUPER_ADMIN, toată platforma). Folosești rapoartele pentru deciziile strategice: ce firme au nevoie de atenție, câte instruiri s-au desfășurat într-o perioadă, unde sunt aglomerate expirările de IP.',
        'Global reports give you a portfolio-wide overview (for SSM_COMPANY_ADMIN, the client companies you serve; for SUPER_ADMIN, the whole platform). You use reports for strategic decisions: which companies need attention, how many trainings were held in a period, where IP expirations cluster.'
      ),
      p(
        'Secțiunea de rapoarte este în dezvoltare activă — apar treptat indicatori noi. În versiunea curentă găsești statistici agregate de bază: număr total de firme, angajați, cursuri desfășurate și rate de finalizare. Urmează rapoarte detaliate cu filtre pe perioadă, tip de curs și firmă.',
        'The reports section is under active development — new indicators are added progressively. In the current version you find basic aggregate stats: total companies, employees, trainings held, and completion rates. Detailed reports with period, course type, and company filters are coming next.'
      ),
      tip(
        'Pentru rapoarte imediate per firmă client, folosește pagina firmei respective — acolo ai deja semaforizarea IP, statusul angajaților și toate fișele generate. Rapoartele globale sunt pentru privirea „de sus", nu pentru analiza punctuală a unei firme.',
        'For immediate per-company reports, use the respective company page — you already have the IP traffic-light, employee status and all generated sheets there. Global reports are for the "top-down" view, not for pinpoint analysis of a single company.'
      ),
    ],
  },

  // ── ADMINISTRARE ───────────────────────────────────────────────────────
  {
    id: 'global-search',
    category: 'admin',
    roles: ['SUPER_ADMIN'],
    title: { ro: 'Căutare globală', en: 'Global search' },
    content: [
      p(
        'Funcția de căutare globală îți permite să găsești rapid o firmă, un utilizator sau un curs oriunde în platformă, fără să navighezi prin arborescența de firme SSM → client → departamente. Accesezi căutarea din meniul lateral, sub iconul de lupă.',
        'Global search lets you quickly find a company, user or course anywhere on the platform, without navigating through the SSM → client → departments tree. Access it from the sidebar, under the magnifying glass icon.'
      ),
      h('Ce poți căuta', 'What you can search for'),
      list(
        [
          'Firme după nume, CUI sau parte din denumire.',
          'Utilizatori după nume, prenume sau email.',
          'Cursuri după titlu sau tip.',
        ],
        [
          'Companies by name, VAT code or partial name.',
          'Users by first name, last name or email.',
          'Courses by title or type.',
        ]
      ),
      p(
        'Rezultatele sunt grupate pe categorii, iar din fiecare rezultat intri direct pe fișa respectivă (pagina firmei, profilul utilizatorului, editorul cursului). Util mai ales când primești un ticket de suport care menționează un email sau un CUI și nu știi din care firmă vine.',
        "Results are grouped by category, and from each result you jump straight to the respective profile (company page, user profile, course editor). Especially useful when you receive a support ticket mentioning an email or VAT code and you don't know which company it belongs to."
      ),
      tip(
        'Căutarea globală este rezervată SUPER_ADMIN — administratorii firmelor SSM au variante mai limitate („Caută firme client" în meniul lor). Este intenționat, ca să nu expunem date cross-firme.',
        'Global search is SUPER_ADMIN-only — SSM company administrators have more limited variants ("Search client companies" in their menu). This is intentional, to avoid exposing cross-company data.'
      ),
    ],
  },

  // ── FAQ ────────────────────────────────────────────────────────────────
  {
    id: 'faq-password',
    category: 'faq',
    roles: ALL_ROLES,
    title: { ro: 'Mi-am uitat parola', en: 'I forgot my password' },
    content: [
      p(
        'Dacă nu îți mai amintești parola, nu e o problemă — ți-o poți reseta singur din pagina de login.',
        "If you no longer remember your password, don't worry — you can reset it yourself from the login page."
      ),
      steps(
        [
          'Deschide pagina de login a platformei.',
          'Apasă „Am uitat parola" sub câmpul de parolă.',
          'Introdu emailul cu care ești înregistrat și apasă „Trimite".',
          'Verifică-ți emailul — vei primi un link valabil un timp limitat.',
          'Apasă linkul, setează o parolă nouă (minim 8 caractere) și confirmă.',
          'Revino la pagina de login și autentifică-te cu parola nouă.',
        ],
        [
          'Open the platform login page.',
          'Press "Forgot password" under the password field.',
          'Enter the email you are registered with and press "Send".',
          'Check your email — you will receive a link valid for a limited time.',
          'Click the link, set a new password (at least 8 characters) and confirm.',
          'Return to the login page and sign in with the new password.',
        ]
      ),
      tip(
        'Dacă nu primești emailul în câteva minute, verifică folderul Spam/Junk. Dacă tot nu ajunge, contactează administratorul firmei pentru a confirma că adresa ta de email este corectă în sistem.',
        "If you don't receive the email within a few minutes, check your Spam/Junk folder. If it still doesn't arrive, contact your company administrator to confirm your email address is correct in the system."
      ),
    ],
  },
  {
    id: 'faq-certificate-missing',
    category: 'faq',
    roles: ['EMPLOYEE', 'COMPANY_ADMIN', 'DIRECTOR_GENERAL'],
    title: { ro: 'Nu găsesc certificatul meu', en: "I can't find my certificate" },
    content: [
      p(
        'Certificatul pentru un curs apare în „Certificările mele" doar după ce ai finalizat complet cursul — și anume: ai parcurs toate modulele, ai promovat testul final și ai semnat fișa de instruire. Dacă unul dintre acești pași lipsește, certificatul nu este generat.',
        'A course certificate appears in "My certificates" only after you have fully completed the course — namely: you watched all modules, passed the final test, and signed the training sheet. If any of these steps is missing, the certificate is not generated.'
      ),
      h('Verifică în ordine', 'Check in order'),
      steps(
        [
          'Intră în „Cursurile mele" și verifică statusul cursului — dacă e „În desfășurare", mai ai de făcut pași.',
          'Dacă statusul e „Picat", reia testul final din pagina cursului.',
          'Dacă statusul e „Finalizat" dar certificatul nu apare, verifică dacă ai semnat fișa — semnarea fișei este ultimul pas.',
          'Dacă toate sunt OK și totuși lipsește, ieși din cont și reintră — uneori cache-ul browserului întârzie afișarea.',
        ],
        [
          'Go to "My trainings" and check the course status — if it\'s "In progress", you still have steps left.',
          'If the status is "Failed", retake the final test from the course page.',
          'If the status is "Completed" but the certificate is missing, check whether you signed the sheet — signing the sheet is the last step.',
          "If everything looks fine and it's still missing, log out and back in — sometimes browser cache delays the display.",
        ]
      ),
      tip(
        'Dacă după toate verificările certificatul încă nu apare, contactează responsabilul SSM sau administratorul firmei — ei pot verifica în sistem dacă s-a produs o eroare la generare și pot regenera documentul.',
        "If after all checks the certificate still doesn't show up, contact the SSM officer or company administrator — they can verify in the system if a generation error occurred and regenerate the document."
      ),
    ],
  },
];

// Etichetele (flag-uri boolean) pe care le recunoaștem pe un user.
// Orice câmp adăugat aici poate fi folosit în `onlyLabels` (blocuri)
// sau în `requireLabelsForRoles` (secțiuni).
export const USER_LABELS = [
  'canManageEmployees', // HR
  'managesDepartment', // CLM
  'isSSMInstructor', // Instructor SSM (ca angajat)
  'isCadruTehnicPSI', // Cadru tehnic PSI
  'hasEPI', // Beneficiar EPI
];

// Helper: verifică dacă un bloc e vizibil pentru utilizatorul curent (rol + etichete).
// `onlyRoles` — listă de roluri pentru care apare blocul. Dacă e setat, user.role TREBUIE să fie în ea.
// `onlyLabels` — listă de etichete. Dacă e setată, user trebuie să aibă MĂCAR una.
// Ambele pot coexista; atunci sunt AND între ele.
export const isBlockVisible = (block, user) => {
  const role = typeof user === 'string' ? user : user?.role;
  const labels = typeof user === 'string' ? {} : user || {};
  if (block.onlyRoles && !block.onlyRoles.includes(role)) return false;
  if (block.onlyLabels && !block.onlyLabels.some((l) => labels[l])) return false;
  return true;
};

// Helper: verifică dacă o secțiune e vizibilă pentru utilizatorul curent.
// `section.roles` — roluri care au acces.
// `section.requireLabelsForRoles` — map opțional { rol: [labels] }. Pentru rolurile enumerate aici,
// user-ul TREBUIE să aibă măcar una din etichete ca să vadă secțiunea. Rolurile care nu apar în map
// văd secțiunea fără condiție de etichetă.
export const isSectionVisible = (section, user) => {
  const role = user?.role;
  if (!role || !section.roles.includes(role)) return false;
  const required = section.requireLabelsForRoles?.[role];
  if (required && required.length) {
    return required.some((l) => user[l]);
  }
  return true;
};

// Normalizează textul pentru căutare: lowercase + elimină diacriticele,
// astfel încât „fisa" să match-uiască „fișa", „semnatura" să prindă „semnătură" etc.
const normalize = (text) =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// Construiește „haystack"-ul pe care se face căutarea pentru o secțiune:
// concatenăm titlul, cuvintele-cheie (dacă există) și tot conținutul vizibil pentru utilizator.
const buildSearchHaystack = (section, user, lang) => {
  const parts = [];
  parts.push(section.title[lang] || section.title.ro || '');
  parts.push(section.title.ro || '');
  parts.push(section.title.en || '');
  if (section.keywords) {
    parts.push(...(section.keywords[lang] || section.keywords.ro || []));
    if (lang === 'ro' && section.keywords.en) parts.push(...section.keywords.en);
    if (lang === 'en' && section.keywords.ro) parts.push(...section.keywords.ro);
  }
  section.content.forEach((block) => {
    if (!isBlockVisible(block, user)) return;
    if (block.type === 'list' || block.type === 'steps') {
      parts.push(...(block[lang] || block.ro || []));
    } else {
      parts.push(block[lang] || block.ro || '');
    }
  });
  return normalize(parts.join(' \n '));
};

// Helper: returnează secțiunile vizibile pentru utilizator și, opțional, filtrate după un query.
// `user` este obiectul din AuthContext (conține role + flag-urile canManageEmployees etc.).
// Query-ul este spart pe cuvinte și fiecare cuvânt trebuie să apară undeva în secțiune (AND pe tokeni).
export const filterSections = (user, query, lang = 'ro') => {
  const tokens = normalize((query || '').trim())
    .split(/\s+/)
    .filter((t) => t.length > 0);
  return MANUAL_SECTIONS.filter((s) => {
    if (!isSectionVisible(s, user)) return false;
    if (tokens.length === 0) return true;
    const haystack = buildSearchHaystack(s, user, lang);
    return tokens.every((tok) => haystack.includes(tok));
  });
};

// Helper: grupează secțiunile pe categorii, păstrând ordinea din MANUAL_CATEGORIES.
export const groupByCategory = (sections) => {
  const map = new Map(MANUAL_CATEGORIES.map((c) => [c.id, { ...c, sections: [] }]));
  sections.forEach((s) => {
    if (map.has(s.category)) map.get(s.category).sections.push(s);
  });
  return Array.from(map.values()).filter((c) => c.sections.length > 0);
};
